import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, ShieldAlert, Shield, Cpu, Network, Database, Lock, Edit3, Save, FileText, Globe, CheckCircle, ShieldCheck, Flame, RefreshCw, Zap, TrendingUp, Compass, Users, Plus, Trash2, ArrowUp, ArrowDown, QrCode, ShoppingBag, Keyboard, Sliders, Mic, Tv, Mail, LogOut, Settings } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, AreaChart, Area, CartesianGrid, Legend } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSiteContent } from '../contexts/SiteContentContext';
import { generateQRBlocks } from './NexoraXPaymentGateway';
import { BrandingTerminal } from './BrandingTerminal';
import { InvoiceLedger } from './InvoiceLedger';
import { ClientDealRegistry } from './ClientDealRegistry';
import { TaxAccountingNode } from './TaxAccountingNode';
import { RevenueTicker } from './RevenueTicker';
import { SecurePaymentLiveBalancePipelineNode } from './SecurePaymentLiveBalancePipelineNode';
import { RealTimeRevenueDashboard } from './RealTimeRevenueDashboard';
import { RevenueD3Chart } from './RevenueD3Chart';
import { ReconnaissanceNode } from './ReconnaissanceNode';
import { VideoGeneratorNode } from './VideoGeneratorNode';
import { VoiceGeneratorNode } from './VoiceGeneratorNode';
import { RealityAuditTerminal } from './RealityAuditTerminal';
import { HolographicDatabase } from './HolographicDatabase';
import { ZakatFlowDiagram } from './ZakatFlowDiagram';
import { OpenBankingConsole } from './OpenBankingConsole';
import { NexoraXCommandLedgerConsole } from './NexoraXCommandLedgerConsole';
import { VoiceCommandManager } from './VoiceCommandManager';
import { VoiceMetricsDashboard } from './VoiceMetricsDashboard';
import { ProductDatabaseManager } from './ProductDatabaseManager';
import { NexoraXHotkeysManager } from './NexoraXHotkeysManager';
import { NexoraXSecurityTerminal } from './NexoraXSecurityTerminal';
import { SecurityAuditLog } from './SecurityAuditLog';
import { EmpireExpansionEngine } from '../lib/NexoraxMasterExpansionMatrix';
import { NexoraxNode51Engine } from '../lib/Node51CommercialGovernance';
import { IdentityEngine } from '../lib/PremierHubSyndicationMatrix';
import { ActiveForgeEngine } from '../lib/NexoraxVideoForgeEngine';
import { BillingMatrix } from '../lib/NexoraxDualBillingEngine';
import { AscensionEngine } from '../lib/Node52AscensionMatrix';
import { EmpireFinancials } from '../lib/NexoraxFinancialRecovery';
import { CATEGORIES } from '../data';
import { NexoraXLatencyMonitor } from './NexoraXLatencyMonitor';
import { ErrorBoundary } from './ErrorBoundary';
import { NexusDigitalProductFactory } from './NexusDigitalProductFactory';
import { DigitalProduct, INITIAL_DIGITAL_PRODUCTS } from '../data/nexusDigitalProducts';
import { NexoraXOperatorConsole } from './NexoraXOperatorConsole';
const GrantFundingPack = React.lazy(() => import('./GrantFundingPack').then(m => ({ default: m.GrantFundingPack })));
import { CampaignDispatchConsole } from './CampaignDispatchConsole';
import { SocialConnectorsConsole } from './SocialConnectorsConsole';
import { SecretaryNode } from './SecretaryNode';
import { CosmicLandingTabPanel } from './CosmicLandingTabPanel';
import { CosmicTabPanel } from './CosmicTabPanel';

const threatData = [
  { type: 'DDoS', count: 42, color: '#f43f5e' },
  { type: 'SQLi', count: 18, color: '#3b82f6' },
  { type: 'Phishing', count: 55, color: '#eab308' },
  { type: 'Ping Scan', count: 120, color: '#22c55e' },
  { type: 'Auth Brute', count: 24, color: '#a855f7' },
];

// Map recentering controller helper component
function RecenterMap({ lat, lng, selection }: { lat: number; lng: number; selection: string }) {
  const map = useMap();
  useEffect(() => {
    // Smooth pan to coordinates when selected sector changes
    map.setView([lat, lng], map.getZoom(), { animate: true, duration: 1.5 });
  }, [lat, lng, map, selection]);
  return null;
}

// Custom cyberpulse Leaflet marker generator
const createCyberMarker = (nodeId: string, color: string, isSelected: boolean, state: 'SECURED' | 'ACTIVE_ROUTING' | 'BLOCKED' = 'SECURED') => {
  let stateBg = '#059669'; // Emerald-600 (NexoraX Secured Cash)
  let stateGlowColor = 'rgba(16, 185, 129, 0.6)';
  let stateIconSvg = '';

  if (state === 'BLOCKED') {
    stateBg = '#e11d48'; // Rose-600 (Threat Blocked / Expelled)
    stateGlowColor = 'rgba(244, 63, 94, 0.7)';
    stateIconSvg = `
      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" opacity="0.3"/>
        <line x1="18" y1="6" x2="6" y2="18" stroke="#ffffff" stroke-width="5" />
      </svg>
    `;
  } else if (state === 'ACTIVE_ROUTING') {
    stateBg = '#2563eb'; // Blue-600 (Active Billing Velocity)
    stateGlowColor = 'rgba(59, 130, 246, 0.7)';
    stateIconSvg = `
      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" style="animation: pulse 1s infinite;">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    `;
  } else {
    // SECURED - NexoraX Cash Lock
    stateBg = '#059669'; // Emerald-600 (Secured finance Assets)
    stateGlowColor = 'rgba(16, 185, 129, 0.6)';
    stateIconSvg = `
      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    `;
  }

  return L.divIcon({
    className: 'custom-cyber-marker',
    html: `
      <div style="position: relative; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px;">
        <!-- Pulsing radial beacon waves -->
        <span style="position: absolute; display: inline-flex; height: 32px; width: 32px; border-radius: 9999px; background-color: ${color}; filter: blur(2px); animation: ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite; opacity: 0.35;"></span>
        ${isSelected ? `<span style="position: absolute; display: inline-flex; height: 40px; width: 40px; border-radius: 9999px; border: 1.5px dashed ${color}; opacity: 0.6; animation: spin 4s linear infinite;"></span>` : ''}
        
        <!-- Core signal center point -->
        <span style="position: relative; display: inline-flex; border-radius: 9999px; height: ${isSelected ? '14px' : '10px'}; width: ${isSelected ? '14px' : '10px'}; background-color: ${color}; border: 1.5px solid #ffffff; box-shadow: 0 0 10px ${color}"></span>
        
        <!-- Small visual indicator child element to show node state (SECURED, ACTIVE_ROUTING, BLOCKED) -->
        <span class="custom-cyber-marker-indicator" style="
          position: absolute;
          top: 3.5px;
          right: 3.5px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          border-radius: 9999px;
          background-color: ${stateBg};
          border: 1px solid #ffffff;
          box-shadow: 0 0 8px ${stateGlowColor};
          z-index: 10;
        ">
          ${stateIconSvg}
        </span>

        <!-- Dynamic QR trigger button inside .custom-cyber-marker as explicitly requested -->
        ${nodeId !== 'IN_ROGUE' ? `
        <button 
          type="button"
          onclick="event.stopPropagation(); if (window.fetchEmpireNodeQr) window.fetchEmpireNodeQr('${nodeId}');"
          title="Fetch Core Gateways QR"
          style="
            position: absolute;
            bottom: -5px;
            right: -5px;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 16px;
            border-radius: 4px;
            background-color: #020617;
            border: 1px solid #334155;
            color: #94a3b8;
            cursor: pointer;
            z-index: 30;
            box-shadow: 0 2px 4px rgba(0,0,0,0.5);
            outline: none;
          "
          class="cyber-qr-sync-btn"
          onmouseover="this.style.borderColor='#fbbf24'; this.style.color='#fbbf24'; this.style.transform='scale(1.1)';"
          onmouseout="this.style.borderColor='#334155'; this.style.color='#94a3b8'; this.style.transform='scale(1)';"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
        </button>
        ` : ''}
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22]
  });
};

export const NexoraxCoreNode: React.FC<{
  onShowFiscalStrike?: () => void;
  onShowLegalStrike?: () => void;
  onShowArchitectVoice?: () => void;
  activeLandingTab?: string | null;
}> = ({ onShowFiscalStrike, onShowLegalStrike, onShowArchitectVoice, activeLandingTab }) => {
  const [activeTab, setActiveTab] = useState('IMPERIAL_VAULT');
  const [adminSubTab, setAdminSubTab] = useState<string>('WORKFORCE_OS');
  const [legacyLabTab, setLegacyLabTab] = useState<string>('sec');
  const [crmSendQueueVersion, setCrmSendQueueVersion] = useState(0);
  const [apiConnectorVersion, setApiConnectorVersion] = useState(0);
  
  const [digitalProducts, setDigitalProducts] = useState<DigitalProduct[]>(() => {
    const saved = localStorage.getItem('nexorax_saved_digital_products');
    return saved ? JSON.parse(saved) : INITIAL_DIGITAL_PRODUCTS;
  });

  const [publicInquiries, setPublicInquiries] = useState<any[]>(() => {
    const saved = localStorage.getItem('nexorax_public_inquiries');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('nexorax_saved_digital_products', JSON.stringify(digitalProducts));
  }, [digitalProducts]);

  useEffect(() => {
    localStorage.setItem('nexorax_public_inquiries', JSON.stringify(publicInquiries));
  }, [publicInquiries]);

  const [selectedGrantSubTab, setSelectedGrantSubTab] = useState<'DASHBOARD' | 'COMPANY' | 'PLAN' | 'BUILDING' | 'CASHFLOW' | 'EQUIPMENT' | 'IMPACT' | 'EVIDENCE' | 'LINKS' | 'BANK' | 'JOBS' | 'LETTER' | 'EXPORT'>('DASHBOARD');
  const [currentBgVisualMode, setCurrentBgVisualMode] = useState<'Cinematic Dark' | 'Balanced' | 'Extra Dark' | 'Original'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('nexorax_bg_visual_mode') as any) || 'Cinematic Dark';
    }
    return 'Cinematic Dark';
  });

  useEffect(() => {
    if (activeLandingTab === 'Grant Funding Pack' || activeLandingTab === 'FUNDING_GRANTS') {
      setActiveTab('FUNDING_GRANTS');
    } else if (activeLandingTab === 'Admin Panel' || activeLandingTab === 'ADMIN_PANEL') {
      setActiveTab('ADMIN_PANEL');
    }
  }, [activeLandingTab]);

  const [gridOpacity, setGridOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_grid_opacity');
      return saved !== null ? parseFloat(saved) : 0.3;
    }
    return 0.3;
  });

  const handleGridOpacityChange = (val: number) => {
    setGridOpacity(val);
    localStorage.setItem('nexorax_grid_opacity', val.toString());
    window.dispatchEvent(new CustomEvent('nexorax-grid-opacity', { detail: val }));
  };

  // Sync state for NexoraX Marketplace toggle
  const [isMarketplaceActive, setIsMarketplaceActive] = useState(
    activeLandingTab === 'NexoraX Marketplace'
  );

  useEffect(() => {
    setIsMarketplaceActive(activeLandingTab === 'NexoraX Marketplace');
  }, [activeLandingTab]);

  useEffect(() => {
    const handleTabChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setIsMarketplaceActive(customEvent.detail === 'NexoraX Marketplace');
      }
    };
    window.addEventListener('nexorax-landing-tab-changed', handleTabChange);
    return () => {
      window.removeEventListener('nexorax-landing-tab-changed', handleTabChange);
    };
  }, []);

  // Hotkey Active state tracking
  const [NexoraXKeys, setNexoraXKeys] = useState<{ marketplace: string; admin: string; help: string }>({
    marketplace: 'Alt + M',
    admin: 'Alt + A',
    help: 'Alt + K',
  });

  useEffect(() => {
    const getHotkeyString = (hk: any) => {
      if (!hk) return '';
      const parts = [];
      if (hk.ctrlKey) parts.push('Ctrl');
      if (hk.altKey) parts.push('Alt');
      if (hk.shiftKey) parts.push('Shift');
      if (hk.metaKey) parts.push('Meta');
      
      let keyName = hk.key.toUpperCase();
      if (keyName === ' ') keyName = 'Space';
      parts.push(keyName);
      return parts.join(' + ');
    };

    const updateActiveKeys = () => {
      try {
        const cached = localStorage.getItem('nexorax_NexoraX_hotkeys');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            const mktHk = parsed.find((h: any) => h.action === 'TOGGLE_MARKETPLACE_VIEW' || h.id === 'toggle-marketplace');
            const admHk = parsed.find((h: any) => h.action === 'TOGGLE_MARKETPLACE_ADMIN' || h.id === 'toggle-admin');
            const helpHk = parsed.find((h: any) => h.action === 'TOGGLE_HOTKEY_HELP' || h.id === 'toggle-help');
            
            setNexoraXKeys({
              marketplace: mktHk ? getHotkeyString(mktHk) : 'Alt + M',
              admin: admHk ? getHotkeyString(admHk) : 'Alt + A',
              help: helpHk ? getHotkeyString(helpHk) : 'Alt + K',
            });
          }
        }
      } catch (err) {
        console.error("Error updating NexoraX hotkeys in CoreNodeDashboard:", err);
      }
    };

    updateActiveKeys();
    window.addEventListener('nexorax-hotkeys-reloaded', updateActiveKeys);
    return () => {
      window.removeEventListener('nexorax-hotkeys-reloaded', updateActiveKeys);
    };
  }, []);
  const [refreshCounter, setRefreshCounter] = useState(10);
  const [activeReceipt, setActiveReceipt] = useState<string | null>(null);
  const [capitalRecoveryStatus, setCapitalRecoveryStatus] = useState<'IDLE' | 'SYNCING' | 'SYNCED'>('IDLE');
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const [suggestionLoading, setSuggestionLoading] = useState<boolean>(false);
  const [suggestionStatus, setSuggestionStatus] = useState<string>('');
  
  // Dynamic QR Code Overlay State
  const [activeQrModalData, setActiveQrModalData] = useState<{
    isOpen: boolean;
    gateway: string;
    label: string;
    link: string;
    qrAsset: string;
    note: string;
    isLoading: boolean;
  } | null>(null);

  const fetchGatewayQr = async (nodeId: string) => {
    let gw = 'SecurePayment';
    if (nodeId === 'SecurePayment_BANK' || nodeId === 'SecurePayment_BANK_SETTLEMENT') gw = 'SecurePayment';
    else if (nodeId === 'UK' || nodeId === 'PK') gw = 'stripe';
    else if (nodeId === 'US') gw = 'gocardless';
    else gw = 'paypal';

    setActiveQrModalData({
      isOpen: true,
      gateway: gw,
      label: gw.toUpperCase(),
      link: '',
      qrAsset: '',
      note: '',
      isLoading: true
    });

    try {
      const res = await fetch(`/api/empire/qr?gateway=${gw}`);
      const data = await res.json();
      if (data.status === 'success') {
        setActiveQrModalData({
          isOpen: true,
          gateway: gw,
          label: data.label,
          link: data.link,
          qrAsset: data.qrAsset,
          note: data.note,
          isLoading: false
        });
      } else {
        throw new Error(data.message || 'Verification Layer Error');
      }
    } catch (err) {
      console.error(err);
      setActiveQrModalData(prev => prev ? {
        ...prev,
        isLoading: false,
        note: 'Failed to authenticate secure billing routing.'
      } : null);
    }
  };

  useEffect(() => {
    (window as any).fetchEmpireNodeQr = (nodeId: string) => {
      fetchGatewayQr(nodeId);
    };
    return () => {
      delete (window as any).fetchEmpireNodeQr;
    };
  }, []);

  const fetchAdminAiSuggestion = async (subTab: string) => {
    setSuggestionLoading(true);
    try {
      const response = await fetch('/api/admin/ai-suggestion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activeTab: subTab === 'CORE_LEDGER' ? 'Core Ledger & NexoraX Tax Matrix' : subTab === 'PRODUCT_MANAGER' ? 'Product Database Manager' : subTab === 'VOICE_METRICS' ? 'Voice Metrics Trend Analysis' : subTab === 'SECURITY_LOGS' ? 'NexoraX Security Events & System Access Logs Terminal' : 'Revenue Insights D3 Engine',
          contextData: {
            cpuLoad: '42%',
            memory: '14.2GB',
            dbLatency: '18ms',
            firewall: 'STRICT',
            sortCode: 'Verified Payment Configuration',
            account: 'Secure Billing Provider',
            netSettlement: '£2,227,800',
            grossHarvest: '£2,820,000',
            activeSection: subTab,
          }
        })
      });
      const data = await response.json();
      if (data.status === 'success' || data.status === 'fallback') {
        setAiSuggestion(data.suggestion);
        setSuggestionStatus(data.status);
      }
    } catch (err) {
      console.error("Failed to fetch AI suggestions:", err);
    } finally {
      setSuggestionLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ADMIN_PANEL') {
      fetchAdminAiSuggestion(adminSubTab);
    }
  }, [activeTab, adminSubTab]);

  const { content, setContent } = useSiteContent();

  // Geographic Heatmap & Click-Through density states
  const [heatmapFilter, setHeatmapFilter] = useState<'Secure Invoice Register' | 'MNDA' | 'PAYMENT' | 'BLOCK'>('Secure Invoice Register');
  const [selectedSector, setSelectedSector] = useState<string>('UK');
  const [focalIntensity, setFocalIntensity] = useState<'NORMAL' | 'HIGH' | 'EXTREME'>('EXTREME');
  const [simulationPulse, setSimulationPulse] = useState<boolean>(false);
  const [heatmapToast, setHeatmapToast] = useState<string | null>(null);
  const [selectedBusinessSector, setSelectedBusinessSector] = useState<'ALL' | 'CONSTRUCTION' | 'TAKEAWAYS' | 'FINANCE'>('ALL');
  const [densityWeights, setDensityWeights] = useState<Record<string, number>>({
    UK: 94,
    EU: 82,
    PK: 78,
    US: 61,
    ASIA: 49,
    CLOUD_NODES: 1,
    SecurePayment_BANK: 100
  });
  
  const [clickLogs, setClickLogs] = useState([
    { id: 'cl-1', candidate: 'Eleanor Vance', ip: '81.104.22.182', zone: 'United Kingdom (Dunfermline)', action: 'Secure Invoice Register Contract Sign Attempt', device: 'macOS / Safari', time: '12s ago', status: '0.8s SAFE' },
    { id: 'cl-2', candidate: 'Claire Jenkins', ip: '82.44.110.12', zone: 'United Kingdom (London)', action: 'Briefing Agenda Load', device: 'iOS / Chrome', time: '1m ago', status: '0.8s SAFE' },
    { id: 'cl-3', candidate: 'Staff Coordinator (PK Remote)', ip: '110.37.12.59', zone: 'Pakistan (Lahore)', action: 'Pakistan_Node_Sync Handshake', device: 'Linux / Firefox', time: '3m ago', status: '0.8s SAFE' },
    { id: 'cl-4', candidate: 'APNS Push Relay', ip: '192.0.2.8', zone: 'Dublin Cloud Node', action: 'Background Sync Check', device: 'Cloud Worker Agent', time: '15m ago', status: '0.8s SAFE' },
    { id: 'cl-5', candidate: 'Helen Saunders', ip: '51.141.22.105', zone: 'United Kingdom (Edinburgh)', action: 'MTA Outbound Click-Through', device: 'Windows / Edge', time: '28m ago', status: '0.8s SAFE' }
  ]);

  const changeBusinessSector = (sector: 'ALL' | 'CONSTRUCTION' | 'TAKEAWAYS' | 'FINANCE') => {
    setSelectedBusinessSector(sector);
    let newWeights: Record<string, number> = { UK: 94, EU: 82, PK: 78, US: 61, ASIA: 49, CLOUD_NODES: 1 };
    
    if (sector === 'CONSTRUCTION') {
      newWeights = { UK: 62, EU: 45, PK: 15, US: 24, ASIA: 11, CLOUD_NODES: 0 };
    } else if (sector === 'TAKEAWAYS') {
      newWeights = { UK: 85, EU: 58, PK: 92, US: 40, ASIA: 65, CLOUD_NODES: 3 };
    } else if (sector === 'FINANCE') {
      newWeights = { UK: 74, EU: 91, PK: 45, US: 88, ASIA: 70, CLOUD_NODES: 5 };
    }
    
    setDensityWeights(newWeights);
    setHeatmapToast(`SECTOR MUTATION TRIGGERED: Active filter set to ${sector}. Spatial weights recalculating.`);
    setTimeout(() => {
      setHeatmapToast(null);
    }, 3000);

    const sectorTemplates: Record<string, typeof clickLogs> = {
      ALL: [
        { id: 'sec-all-1', candidate: 'Eleanor Vance', ip: '81.104.22.182', zone: 'United Kingdom (Dunfermline)', action: 'Secure Invoice Register Contract Sign Attempt', device: 'macOS / Safari', time: '1s ago', status: '0.8s SAFE' },
        { id: 'sec-all-2', candidate: 'Claire Jenkins', ip: '82.44.110.12', zone: 'United Kingdom (London)', action: 'Briefing Agenda Load', device: 'iOS / Chrome', time: '1m ago', status: '0.8s SAFE' },
        { id: 'sec-all-3', candidate: 'Staff Coordinator (PK Remote)', ip: '110.37.12.59', zone: 'Pakistan (Lahore)', action: 'Pakistan_Node_Sync Handshake', device: 'Linux / Firefox', time: '3m ago', status: '0.8s SAFE' }
      ],
      CONSTRUCTION: [
        { id: 'sec-con-1', candidate: 'NexoraX Build Co', ip: '81.189.5.42', zone: 'United Kingdom (Dunfermline)', action: 'Construction Site Bidding Click', device: 'Windows / Edge', time: '2s ago', status: '0.8s SAFE' },
        { id: 'sec-con-2', candidate: 'Al-Farooq Builders', ip: '110.37.104.18', zone: 'Pakistan (Rawalpindi)', action: 'Blueprint Vault Download Trigger', device: 'Android / Chrome', time: '4m ago', status: '0.8s SAFE' },
        { id: 'sec-con-3', candidate: 'Rhine Concrete AG', ip: '46.111.45.92', zone: 'Germany (Hamburg)', action: 'MNDA Delivery Confirmation Click', device: "Safari / macOS", time: '10m ago', status: '0.8s SAFE' }
      ],
      TAKEAWAYS: [
        { id: 'sec-tak-1', candidate: 'Hot & Spicy Dunfermline', ip: '82.44.112.59', zone: 'United Kingdom (Dunfermline)', action: 'Takeaway API Invoice Settlement', device: 'iOS / Chrome', time: 'Instant', status: '0.8s SAFE' },
        { id: 'sec-tak-2', candidate: 'Lahore Karahi Center', ip: '110.40.12.91', zone: 'Pakistan (Lahore)', action: 'Pakistan_Node_Sync Delivery Click', device: 'Linux Terminal', time: '1m ago', status: '0.8s SAFE' },
        { id: 'sec-tak-3', candidate: 'Imperial Pizza Express', ip: '64.225.101.4', zone: 'United States (Boston)', action: 'SecurePayment Merchant Checkout Click', device: 'Enterprise POS', time: '12m ago', status: '0.8s SAFE' }
      ],
      FINANCE: [
        { id: 'sec-fin-1', candidate: 'SecurePayment Liquidity Router', ip: '81.104.22.182', zone: 'United Kingdom (London Central)', action: 'NexoraX 1% Levy Extraction Click', device: 'Secure Payment Provider API Proxy', time: '3s ago', status: '0.8s SAFE' },
        { id: 'sec-fin-2', candidate: 'Frankfurt Gavel Arbitrage', ip: '5.161.42.222', zone: 'Germany (Frankfurt Core)', action: '10% Maintenance & Hosting Fee Settlement Click', device: 'Backend Service VM', time: '22s ago', status: '0.8s SAFE' },
        { id: 'sec-fin-3', candidate: 'Vanguard Sync Validator', ip: '192.0.2.8', zone: 'Dublin Gateway', action: 'Heartbeat Check Sequence', device: 'Sync Worker Service', time: '1m ago', status: '0.8s SAFE' }
      ]
    };
    setClickLogs(sectorTemplates[sector] || sectorTemplates.ALL);
  };

  const triggerTrafficSimulation = () => {
    setSimulationPulse(true);
    setTimeout(() => setSimulationPulse(false), 1200);

    setDensityWeights(prev => ({
      UK: Math.min(100, Math.max(40, prev.UK + Math.floor(Math.random() * 15) - 7)),
      EU: Math.min(100, Math.max(30, prev.EU + Math.floor(Math.random() * 15) - 7)),
      PK: Math.min(100, Math.max(20, prev.PK + Math.floor(Math.random() * 15) - 7)),
      US: Math.min(100, Math.max(15, prev.US + Math.floor(Math.random() * 15) - 7)),
      ASIA: Math.min(100, Math.max(10, prev.ASIA + Math.floor(Math.random() * 15) - 7)),
      CLOUD_NODES: Math.min(30, Math.max(0, (prev.CLOUD_NODES || 0) + Math.floor(Math.random() * 5) - 2))
    }));

    const candidates = ['Eleanor Vance', 'Claire Jenkins', 'Helen Saunders', 'Staff Coordinator (PK Remote)', 'Communications Associate (PK Node)', 'External Client Deal Partner'];
    const ips = ['81.104.22.182', '82.44.110.12', '110.37.12.59', '188.166.12.45', '64.225.101.4', '122.211.5.89'];
    const zones = ['United Kingdom (Dunfermline)', 'United Kingdom (London)', 'Pakistan (Lahore)', 'Germany (Frankfurt)', 'United States (New York)', 'Japan (Tokyo)'];
    const actions = {
      'Secure Invoice Register': 'Secure Invoice Register Contract Ingress Handshake',
      MNDA: 'MNDA Portal Secure Verification Click',
      PAYMENT: 'SecurePayment Provider Transaction Click',
      BLOCK: 'Malicious Firewall Probe Rejected'
    };

    const idx = Math.floor(Math.random() * candidates.length);
    const mockAction = actions[heatmapFilter];

    const newLog = {
      id: `sim-cl-${Math.random().toString(36).substring(2, 6)}`,
      candidate: heatmapFilter === 'BLOCK' ? 'Blocked Probe' : candidates[idx],
      ip: ips[idx],
      zone: zones[idx],
      action: `${mockAction} (${selectedBusinessSector})`,
      device: ['macOS / Chrome', 'iOS / Safari', 'Windows / Edge', 'Linux / Firefox'][Math.floor(Math.random() * 4)],
      time: 'Just now',
      status: heatmapFilter === 'BLOCK' ? 'MITIGATED' : '0.8s SAFE'
    };

    setClickLogs(prev => [newLog, ...prev.slice(0, 5)]);
    
    setHeatmapToast(`TELEMETRY: Simulated high-velocity action registered at ${newLog.zone} [IP: ${newLog.ip}]`);
    setTimeout(() => setHeatmapToast(null), 3000);
  };

  const resetHeatmapData = () => {
    setDensityWeights({
      UK: 45,
      EU: 30,
      PK: 25,
      US: 15,
      ASIA: 10,
      IN_ROGUE: 0
    });
    setClickLogs([]);
    setSelectedBusinessSector('ALL');
    setHeatmapToast("HEATMAP DATA COLD-CLEARED");
    setTimeout(() => setHeatmapToast(null), 2000);
  };

  const [localContent, setLocalContent] = useState(content);
  const [localCategories, setLocalCategories] = useState(content.categories || []);
  const [justSavedId, setJustSavedId] = useState<number | null>(null);
  const [isBackupRunning, setIsBackupRunning] = useState(false);

  const handleInitiateBackup = () => {
    setIsBackupRunning(true);
    
    // Simulate NexoraX Cash-Flow Engine execution
    const fetchGoCardlessSettlements = () => 3697.00;
    const settledRevenue = fetchGoCardlessSettlements();
    const liquidCash = settledRevenue * 0.55; 
    
    const dailyUpdate = {
      date: new Date().toISOString(),
      totalCashInAccount: liquidCash.toFixed(2),
      settlementVelocity: "HIGH_PRIORITY",
      nextHarvestTarget: "2026-05-27T09:00:00Z",
      timestamp: new Date().toISOString(),
      grossHarvest: 3697.00,
      gavelFee: 739.40,
      zakatLevy: 36.97,
      vatCollected: 887.28,
      systemStatus: "AUTONOMOUS_MATRIX_GREEN"
    };

    const blob = new Blob([JSON.stringify(dailyUpdate, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DAILY_CASH_SUMMARY_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    setTimeout(() => {
      setIsBackupRunning(false);
    }, 1500);
  };
  const [pendingChanges, setPendingChanges] = useState(0);

  const [secureInvoiceRegisterCandidate, setSecureInvoiceRegisterCandidate] = useState('');
  const [secureInvoiceRegisterAgency, setSecureInvoiceRegisterAgency] = useState('');
  const [secureInvoiceRegisterEmail, setSecureInvoiceRegisterEmail] = useState('');
  const [mandateGenerated, setMandateGenerated] = useState(false);

  useEffect(() => {
    let count = 0;
    if (localContent.heroPrimaryHook !== content.heroPrimaryHook) count++;
    if (localContent.heroSubHook !== content.heroSubHook) count++;
    if (localContent.globalSupportNumber !== content.globalSupportNumber) count++;
    if (localContent.targetDirective !== content.targetDirective) count++;
    if (localContent.statusIndicator !== content.statusIndicator) count++;
    if (JSON.stringify(localCategories) !== JSON.stringify(content.categories)) count++;
    setPendingChanges(count);
  }, [localContent, content, localCategories]);

  const handleCommit = () => {
    setContent({ ...localContent, categories: localCategories });
    setPendingChanges(0);
  };

  useEffect(() => {
    setLocalContent(content);
    setLocalCategories(content.categories || []);
  }, [content]);

  useEffect(() => {
    const timer = setInterval(() => {
      setRefreshCounter(prev => prev <= 0 ? 10 : prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleGlobalKeys = (e: KeyboardEvent) => {
      const isInputActive = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';

      // Load hotkeys from storage to stay interactive with the configurator tool
      let activeHotkeys = [];
      const defaultMappings = [
        {
          actionId: 'GOTO_MARKETPLACE_MANAGER',
          key: 'm',
          altKey: true,
          ctrlKey: false,
          shiftKey: false
        },
        {
          actionId: 'GOTO_NEXORAX_MARKETPLACE',
          key: 'p',
          altKey: true,
          ctrlKey: false,
          shiftKey: false
        }
      ];

      try {
        const stored = localStorage.getItem('nexorax_admin_hotkeys');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            // merge default mappings with loaded mappings to guarantee completeness
            const merged = [...defaultMappings];
            parsed.forEach(storedItem => {
              const index = merged.findIndex(d => d.actionId === storedItem.actionId);
              if (index !== -1) {
                merged[index] = {
                  ...merged[index],
                  key: storedItem.key,
                  altKey: storedItem.altKey,
                  ctrlKey: storedItem.ctrlKey,
                  shiftKey: storedItem.shiftKey
                };
              }
            });
            activeHotkeys = merged;
          }
        }
      } catch (err) {}

      if (!activeHotkeys || activeHotkeys.length === 0) {
        activeHotkeys = defaultMappings;
      }

      // Check specifically for active GOTO_MARKETPLACE_MANAGER mapping
      const mapping = activeHotkeys.find((h: any) => h.actionId === 'GOTO_MARKETPLACE_MANAGER');
      if (mapping) {
        const matchesKey = e.key.toLowerCase() === mapping.key.toLowerCase();
        const matchesAlt = e.altKey === mapping.altKey;
        const matchesCtrl = e.ctrlKey === mapping.ctrlKey;
        const matchesShift = e.shiftKey === mapping.shiftKey;

        if (matchesKey && matchesAlt && matchesCtrl && matchesShift) {
          if (isInputActive) {
            // Typing in input, block keydown matching unless modifier like Alt is active or Esc key
            if (mapping.key !== 'Escape' && !mapping.altKey && !mapping.ctrlKey) {
              return;
            }
          }
          e.preventDefault();
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('nexorax-trigger-landing-tab', { detail: null }));
          setActiveTab('ADMIN_PANEL');
          setAdminSubTab('PRODUCT_MANAGER');
          return;
        }
      }

      // Check specifically for active GOTO_NEXORAX_MARKETPLACE mapping
      const mktMapping = activeHotkeys.find((h: any) => h.actionId === 'GOTO_NEXORAX_MARKETPLACE');
      if (mktMapping) {
        const matchesKey = e.key.toLowerCase() === mktMapping.key.toLowerCase();
        const matchesAlt = e.altKey === mktMapping.altKey;
        const matchesCtrl = e.ctrlKey === mktMapping.ctrlKey;
        const matchesShift = e.shiftKey === mktMapping.shiftKey;

        if (matchesKey && matchesAlt && matchesCtrl && matchesShift) {
          if (isInputActive) {
            if (mktMapping.key !== 'Escape' && !mktMapping.altKey && !mktMapping.ctrlKey) {
              return;
            }
          }
          e.preventDefault();
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('nexorax-hotkey-action', { detail: { action: 'TOGGLE_MARKETPLACE_VIEW' } }));
          return;
        }
      }

      // Legacy key fallback
      if (!isInputActive) {
        if (e.key === '8' || (e.altKey && e.key.toLowerCase() === 'h')) {
          e.preventDefault();
          setActiveTab('HERMES_GRID');
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeys, true);
    return () => window.removeEventListener('keydown', handleGlobalKeys, true);
  }, []);

  const tabs = [
    { id: 'NEXORAX_BANK', label: '01. NEXORAX BANK' },
    { id: 'IMPERIAL_VAULT', label: '02. NexoraX Resource Library' },
    { id: 'MANAGEMENT_DECK', label: '03. MANAGEMENT DECK' },
    { id: 'BRAND_HUB', label: '04. BRAND HUB' },
    { id: 'CATEGORIES', label: '05. CATEGORIES' },
    { id: 'SYSTEM_STATE', label: '06. SYSTEM STATE' },
    { id: 'SITE_EDITOR', label: '07. SITE EDITOR' },
    { id: 'Secure Invoice Register_OPERATIONS', label: '08. Secure Invoice Register OPERATIONS' },
    { id: 'BRANDING_COMMAND', label: '09. BRANDING COMMAND' },
    { id: 'ADMIN_PANEL', label: '10. ADMIN PANEL' },
    { id: 'INVOICE_LEDGER', label: '11. INVOICE LEDGER' },
    { id: 'DEAL_REGISTRY', label: '12. DEAL REGISTRY' },
    { id: 'RECONNAISSANCE_NODE', label: '13. RECONNAISSANCE NODE' },
    { id: 'VIDEO_GENERATOR', label: '14. CINEMATIC VIDEO FORGE' },
    { id: 'VOICE_GENERATOR', label: '15. NEURAL TTS FORGE' },
    { id: 'GEOGRAPHIC_ANALYTICS', label: '16. GEOGRAPHIC HEATMAP' },
    { id: 'REALITY_AUDIT', label: '17. REALITY AUDIT (V.1)' },
    { id: 'HERMES_GRID', label: '18. HERMES ACQUISITION GRID' },
    { id: 'NEXORAX_ENGINE', label: '19. THE NEXORAX ENGINE' },
    { id: 'NEXORAX_GENERATOR', label: '20. THE NEXORAX GENERATOR' },
    { id: 'COMMAND_LEDGER', label: '21. COMMAND LEDGER' },
    { id: 'FUNDING_GRANTS', label: '22. FUNDING & GRANTS' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'RECONNAISSANCE_NODE':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <ReconnaissanceNode />
          </motion.div>
        );
      case 'VIDEO_GENERATOR':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <VideoGeneratorNode />
          </motion.div>
        );
      case 'VOICE_GENERATOR':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <VoiceGeneratorNode />
          </motion.div>
        );
      case 'REALITY_AUDIT':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 md:p-10 flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <RealityAuditTerminal />
          </motion.div>
        );
      case 'HERMES_GRID':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 md:p-10 flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <HolographicDatabase initialCategoryId={81} />
          </motion.div>
        );
      case 'NEXORAX_ENGINE':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 md:p-10 flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <HolographicDatabase initialCategoryId={84} />
          </motion.div>
        );
      case 'NEXORAX_GENERATOR':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 md:p-10 flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <HolographicDatabase initialCategoryId={85} />
          </motion.div>
        );
      case 'NEXORAX_BANK':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d] gap-8">
            <ZakatFlowDiagram />

            {/* Persistent Capital Instruments Recovery Segment */}
            <div className="w-full max-w-5xl mx-auto bg-gradient-to-r from-emerald-950/20 to-cyan-950/20 border border-cyan-800/30 rounded-2xl p-6 sm:p-8 space-y-6 text-left relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 blur-[80px] rounded-full pointer-events-none"></div>
              
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-cyan-900/40 pb-4">
                <div>
                  <h3 className="text-sm font-bold text-cyan-400 font-mono uppercase tracking-widest flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-cyan-400 animate-pulse" />
                    V6.2 EMPIRE CAPITAL RECOVERY CONSOLE
                  </h3>
                  <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mt-1">
                    Verifies hardcoded liquidity buffers to bypass session state degradation
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    setCapitalRecoveryStatus('SYNCING');
                    setTimeout(() => {
                      EmpireFinancials.syncToVault();
                      setCapitalRecoveryStatus('SYNCED');
                    }, 1200);
                  }}
                  disabled={capitalRecoveryStatus === 'SYNCING'}
                  className={`px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg border transition-all cursor-pointer flex items-center gap-2 ${
                    capitalRecoveryStatus === 'SYNCED'
                      ? "bg-emerald-950/40 border-emerald-500/40 text-emerald-400"
                      : capitalRecoveryStatus === 'SYNCING'
                      ? "bg-cyan-950/20 border-cyan-800/30 text-cyan-400/50 cursor-not-allowed"
                      : "bg-[#050a18] border-cyan-500/35 text-cyan-400 hover:bg-cyan-950/30"
                  }`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${capitalRecoveryStatus === 'SYNCING' ? 'animate-spin' : ''}`} />
                  {capitalRecoveryStatus === 'SYNCED' ? 'SNAPSHOT VAULT REPLICATED' : capitalRecoveryStatus === 'SYNCING' ? 'REPLICATING SNAPSHOT...' : 'SYNC SNAPSHOT TO VAULT'}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-[#02050c]/90 border border-cyan-950/80 rounded-xl p-5 relative">
                  <span className="text-[9px] font-mono text-cyan-500 font-bold uppercase tracking-[0.2em] block mb-1">
                    PRIMARY SecurePayment CASH RESERVES
                  </span>
                  <span className="text-3xl font-mono font-black text-white block">
                    £{EmpireFinancials.getCoreCapitalSnapshot().primarySecurePaymentSettlement.toLocaleString()}
                  </span>
                  <div className="text-[9px] text-slate-500 font-mono uppercase tracking-wider mt-2 flex justify-between">
                    <span>STATUS: RESTORED & LOCK-IN</span>
                    <span className="text-cyan-400/80">OPERATIONAL CAP</span>
                  </div>
                </div>

                <div className="bg-[#02050c]/90 border border-cyan-950/80 rounded-xl p-5 relative">
                  <span className="text-[9px] font-mono text-indigo-400 font-bold uppercase tracking-[0.2em] block mb-1">
                    NEXORAX COMPREHENSIVE resource
                  </span>
                  <span className="text-3xl font-mono font-black text-white block">
                    £{EmpireFinancials.getCoreCapitalSnapshot().NexoraXInvestmentCap.toLocaleString()}
                  </span>
                  <div className="text-[9px] text-slate-500 font-mono uppercase tracking-wider mt-2 flex justify-between">
                    <span>SECURITY: IMMUTABLE ANCHOR</span>
                    <span className="text-indigo-400/80">GROWTH CAPITAL</span>
                  </div>
                </div>
              </div>

              {capitalRecoveryStatus === 'SYNCED' && (
                <motion.div 
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-emerald-950/20 border border-emerald-500/20 p-3 rounded-lg flex items-center gap-2 text-[10px] text-emerald-400 font-mono uppercase"
                >
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Success // Persistent Ledger mirrored to NexoraX Resource Library (/var/www/nexorax_source/CAPITAL_RECOVERY_SNAPSHOT.json)</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        );

      case 'COMMAND_LEDGER':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] h-full overflow-y-auto">
            <NexoraXCommandLedgerConsole />
          </motion.div>
        );

      case 'FUNDING_GRANTS':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="flex flex-col lg:flex-row flex-1 bg-gradient-to-b from-[#080d1e] to-[#04060d] min-h-[700px] border border-cyan-500/10 rounded-xl overflow-hidden"
          >
            {/* Elegant Out of This Galaxy Left Navigation Sidebar for Funding & Grants pack */}
            <div className="w-full lg:w-72 border-b lg:border-b-0 lg:border-r border-slate-800 bg-[#020205]/90 p-5 space-y-5 shrink-0 font-sans flex flex-col justify-between backdrop-blur-md">
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-purple-500/20">
                  <span className="w-2 h-2 bg-[#00e5ff] rounded-full animate-pulse shadow-[0_0_8px_rgba(0,229,255,0.8)]"></span>
                  <span className="text-white font-mono font-bold text-xs uppercase tracking-wider">GRANT DIRECTORIES</span>
                </div>
                
                <div className="flex flex-col gap-1">
                  {[
                    { id: 'DASHBOARD', label: '0. Dashboard View', desc: 'Summary of readiness' },
                    { id: 'COMPANY', label: '1. Company Details', desc: 'SC887095 registers' },
                    { id: 'PLAN', label: '2. Business Plan', desc: 'Core business model' },
                    { id: 'BUILDING', label: '3. What We Build', desc: 'Deliverables & latency' },
                    { id: 'CASHFLOW', label: '4. 12M Cashflow', desc: 'Revenue & spend charts' },
                    { id: 'EQUIPMENT', label: '5. Equipment & Quotes', desc: 'Hardware & suppliers' },
                    { id: 'IMPACT', label: '6. Turnover Impact', desc: 'KPI growth modeling' },
                    { id: 'EVIDENCE', label: '7. Trading Evidence', desc: 'Incorporation records' },
                    { id: 'LINKS', label: '8. Platform Links', desc: 'Main corporate indexes' },
                    { id: 'BANK', label: '9. Bank Register', desc: 'Redacted cold storage' },
                    { id: 'JOBS', label: '10. Job Creation', desc: 'Local Fife positions' },
                    { id: 'LETTER', label: '11. Cover Letter', desc: 'Fife Council address' },
                    { id: 'EXPORT', label: '12. Adviser Export', desc: 'Finalized ZIP pack' }
                  ].map((subItem) => (
                    <button
                      key={subItem.id}
                      type="button"
                      onClick={() => setSelectedGrantSubTab(subItem.id as any)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-all border ${
                        selectedGrantSubTab === subItem.id
                          ? 'bg-purple-950/45 border-purple-500/40 text-[#c084fc] shadow-[0_0_15px_rgba(168,85,247,0.15)]'
                          : 'bg-transparent border-transparent text-slate-400 hover:bg-slate-900/30 hover:text-slate-200'
                      }`}
                    >
                      <div className="text-[10px] font-mono font-bold uppercase tracking-wider leading-none text-left">
                        {subItem.label}
                      </div>
                      <div className="text-[8px] font-mono text-slate-500 lowercase mt-0.5 text-left">
                        {subItem.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-[#0b041a] border border-purple-900/40 rounded-xl p-3.5 space-y-1 shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
                <span className="text-[9px] font-mono text-purple-400 font-extrabold uppercase tracking-widest block text-left">ADMIN ENCRYPTED SHIELD</span>
                <p className="text-[8px] font-mono text-slate-500 uppercase leading-normal text-left">
                  Fife council economic node compliance verified under SC887095 registry files.
                </p>
              </div>
            </div>

            {/* Content Display Area */}
            <div className="flex-1 p-4 md:p-8 overflow-y-auto max-h-[85vh] custom-scrollbar bg-black/5 text-left">
              <ErrorBoundary>
                <React.Suspense fallback={
                  <div className="bg-slate-950/40 border border-slate-900/60 p-6 rounded-xl flex items-center justify-center space-x-3 text-slate-400 font-mono text-[10px] py-12">
                    <RefreshCw className="w-4 h-4 animate-spin text-[#00e5ff]" />
                    <span>Loading Grant Funding Pack...</span>
                  </div>
                }>
                  <GrantFundingPack 
                    activeLandingTab={activeLandingTab} 
                    initialSubTab={selectedGrantSubTab} 
                    onSubTabChange={(tab) => setSelectedGrantSubTab(tab)} 
                  />
                </React.Suspense>
              </ErrorBoundary>
            </div>
          </motion.div>
        );

      case 'MANAGEMENT_DECK':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d]">
             <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-blue-500 font-bold text-xs uppercase tracking-[0.3em]">MANAGEMENT DECK // FLEET OVERSIGHT</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(59,130,246,0.15)] text-center">
                GLOBAL COMMAND CENTER
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto w-full">
               <div className="lg:col-span-2 bg-black/50 border border-slate-800 rounded-xl p-6">
                 <h3 className="text-blue-400 font-bold text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2"><Network className="w-4 h-4"/> INFRASTRUCTURE NODES</h3>
                 <div className="w-full overflow-x-auto">
                   <table className="w-full text-left font-mono text-[10px] uppercase tracking-wider text-slate-400">
                     <thead>
                       <tr className="border-b border-slate-800">
                         <th className="pb-3 pt-2 px-2">Node ID</th>
                         <th className="pb-3 pt-2 px-2">Classification</th>
                         <th className="pb-3 pt-2 px-2">Status</th>
                         <th className="pb-3 pt-2 px-2">Mirror Sync</th>
                         <th className="pb-3 pt-2 px-2 text-right">Uptime</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-800/50">
                       {[
                         { id: "NODE-01", type: "Franchise Core", status: "Active", sync: "CloudSync-02", uptime: "99.99%" },
                         { id: "NODE-14", type: "Poultry Processing", status: "Active", sync: "CloudSync-02", uptime: "99.98%" },
                         { id: "NODE-22", type: "Agency Backend", status: "Active", sync: "CloudSync-02", uptime: "99.99%" },
                         { id: "NODE-27.1", type: "Capital Vault", status: "Active", sync: "Local Override", uptime: "100.0%" },
                         { id: "NODE-44", type: "Logistics Hub", status: "Pending Update", sync: "Guardian-01", uptime: "98.45%" },
                       ].map(node => (
                         <tr key={node.id} className="hover:bg-blue-900/10 transition-colors">
                           <td className="py-3 px-2 text-white font-bold">{node.id}</td>
                           <td className="py-3 px-2">{node.type}</td>
                           <td className="py-3 px-2 text-emerald-400">{node.status}</td>
                           <td className="py-3 px-2">{node.sync}</td>
                           <td className="py-3 px-2 text-right">{node.uptime}</td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 </div>
               </div>

               <div className="flex flex-col gap-6">
                 <div className="bg-blue-900/10 border border-blue-900/50 rounded-xl p-6 relative overflow-hidden flex-1">
                    <h3 className="text-blue-400 font-bold text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2"><Cpu className="w-4 h-4"/> MIRROR PROTOCOL</h3>
                    <div className="text-[10px] text-slate-300 font-mono leading-relaxed space-y-4">
                      <p>Continuous background file mirroring active between <span className="text-white font-bold">/var/www/nexorax_source</span> and <span className="text-white font-bold">gdrive:Nexorax_Ecosystem</span></p>
                      <div className="flex items-center gap-3 bg-black/40 p-3 rounded border border-blue-900/30">
                        <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)] animate-pulse"></div>
                        <span className="tracking-widest font-bold text-white">CloudSync-02 ONLINE</span>
                      </div>
                    </div>
                 </div>
                 
                 <button 
                   onClick={handleInitiateBackup}
                   disabled={isBackupRunning}
                   className={`text-white font-sans font-bold uppercase tracking-widest text-xs py-4 rounded-xl shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-colors ${isBackupRunning ? 'bg-blue-800 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'}`}
                 >
                   {isBackupRunning ? 'EXPORTING NexoraX Resource Library DATA...' : 'INITIATE GLOBAL BACKUP'}
                 </button>
               </div>
            </div>
          </motion.div>
        );

      case 'BRAND_HUB':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d]">
             <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-rose-500 font-bold text-xs uppercase tracking-[0.3em]">BRAND HUB // COGNITIVE DEFENSE MATRIX</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(244,63,94,0.15)] text-center w-full max-w-4xl">
                IP NEXORAXTY & THREAT ISOLATION
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto w-full">
               <div className="bg-rose-900/10 border border-rose-900/40 rounded-xl p-6">
                 <h3 className="text-rose-400 font-bold text-[10px] uppercase tracking-[0.2em] mb-6 flex items-center gap-2"><ShieldAlert className="w-4 h-4"/> ACTIVE THREAT TRACKING</h3>
                 <div className="space-y-4">
                   {[
                     { ip: '103.119.*.*', loc: 'Maharashtra, IND', target: 'NexoraX.com', status: 'NEUTRALIZED' },
                     { ip: '45.186.*.*', loc: 'Delhi, IND', target: 'Brand Phishing', status: 'ISOLATED' },
                     { ip: '182.72.*.*', loc: 'Karnataka, IND', target: 'Infrastructure Ping', status: 'BLOCKED' },
                   ].map((threat, i) => (
                     <div key={i} className="flex justify-between items-center bg-black/40 border border-rose-900/20 p-3 rounded">
                       <div className="flex flex-col">
                         <span className="text-white font-mono text-[10px] font-bold">{threat.ip}</span>
                         <span className="text-slate-500 font-mono text-[9px] uppercase tracking-widest">{threat.loc} - {threat.target}</span>
                       </div>
                       <span className="text-rose-500 font-mono text-[9px] font-bold tracking-widest bg-rose-900/20 px-2 py-1 rounded">{threat.status}</span>
                     </div>
                   ))}
                 </div>
               </div>

               <div className="bg-black/40 border border-slate-800 rounded-xl p-6 flex flex-col justify-between">
                 <div>
                   <h3 className="text-slate-300 font-bold text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2"><Lock className="w-4 h-4"/> REGULATORY COMPLIANCE CORRIDOR</h3>
                   <p className="text-slate-400 font-mono text-[10px] leading-relaxed mb-6">
                     All computational outputs, operational strategies, and data procedures are strictly aligned with official UK Government regulations and applicable worldwide statutory frameworks.
                   </p>
                   <div className="bg-[#050914] p-4 rounded border border-slate-800">
                     <div className="text-white font-mono text-[10px] font-bold tracking-widest mb-2">COPYRIGHT INJECTION: ACTIVE</div>
                     <p className="text-slate-500 font-mono text-[9px]">NexoraX IP markers generated and appended to all frameworks produced within this session.</p>
                   </div>
                 </div>
                 
                 <div className="mt-6 flex items-center gap-3">
                   <div className="flex-1 h-px bg-slate-800"></div>
                   <div className="text-slate-600 font-mono text-[9px] tracking-widest">DEFENSE GRID SECURE</div>
                   <div className="flex-1 h-px bg-slate-800"></div>
                 </div>
               </div>
            </div>
          </motion.div>
        );

      case 'CATEGORIES':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d]">
             <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-[#a855f7] font-bold text-xs uppercase tracking-[0.3em]">CATEGORIES // ENTERPRISE TAXONOMY</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(168,85,247,0.15)] text-center">
                GLOBAL SECTOR MATRICES
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-[1400px] mx-auto w-full overflow-y-auto pb-20 custom-scrollbar pr-2">
              {content.categories.map((cat, idx) => {
                const colors = [
                  'from-blue-500 to-indigo-600',
                  'from-emerald-500 to-teal-600',
                  'from-amber-500 to-orange-600',
                  'from-rose-500 to-pink-600',
                  'from-lime-500 to-green-600',
                  'from-cyan-500 to-blue-600',
                  'from-purple-500 to-indigo-600',
                  'from-[#e840f3] to-fuchsia-600'
                ];
                const color = colors[idx % colors.length];
                return (
                  <div key={cat.id} className="bg-black/50 border border-slate-800 hover:border-slate-600 transition-colors p-6 rounded-xl flex flex-col items-center justify-center text-center gap-4 group cursor-pointer relative overflow-hidden min-h-[140px]">
                    <div className={`absolute top-0 w-full h-1 bg-gradient-to-r ${color} opacity-50 group-hover:opacity-100 transition-opacity`}></div>
                    <span className="text-slate-500 font-mono text-[10px] font-bold tracking-[0.2em]">CAT-{cat.id.toString().padStart(2, '0')}</span>
                    <span className="text-white font-sans text-xs sm:text-sm font-bold tracking-widest uppercase">{cat.title}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        );

      case 'SYSTEM_STATE':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d] overflow-y-auto">
             <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-cyan-500 font-bold text-xs uppercase tracking-[0.3em]">SYSTEM STATE // HEALTH & SECURITY</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(6,182,212,0.15)] text-center">
                GLOBAL OVERSIGHT MATRIX
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto w-full">
               {/* Health Monitor */}
               <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6">
                 <h3 className="text-cyan-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><Activity className="w-4 h-4"/> NODE HEALTH MONITOR</h3>
                 <div className="space-y-4">
                    {[
                      { name: 'Core Engine (V2)', status: 'Optimal', ping: '12ms', load: 45 },
                      { name: 'SecurePayment Routing API', status: 'Optimal', ping: '28ms', load: 12 },
                      { name: 'NexoraX DB', status: 'Syncing', ping: '8ms', load: 78 },
                      { name: 'CloudSync-02', status: 'Optimal', ping: '15ms', load: 32 },
                    ].map(node => (
                       <div key={node.name} className="flex flex-col gap-2 bg-[#050914] p-3 rounded border border-slate-800/50 relative overflow-hidden">
                         <div className="flex justify-between items-center text-[10px] font-mono relative z-10">
                           <span className="text-slate-300 font-bold tracking-wider">{node.name}</span>
                           <span className={node.status === 'Optimal' ? 'text-emerald-500' : 'text-amber-500'}>{node.status} ({node.ping})</span>
                         </div>
                         <div className="w-full bg-[#1e1e2d] h-1.5 rounded-full overflow-hidden relative z-10 transition-all">
                           <motion.div 
                             initial={{ width: 0 }}
                             animate={{ width: `${node.load}%` }}
                             className={`h-full ${node.load > 70 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                             transition={{ duration: 1, ease: 'easeOut' }}
                           ></motion.div>
                         </div>
                         <div className="absolute top-0 right-0 h-full w-24 bg-cyan-900/10 blur-xl"></div>
                       </div>
                    ))}
                 </div>
               </div>

               {/* Nuremberg Latency Check */}
               <NexoraXLatencyMonitor isInline={true} />

               {/* Threat Log */}
               <div className="bg-rose-900/5 border border-rose-900/30 rounded-xl p-6 flex flex-col gap-4">
                 <h3 className="text-rose-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><ShieldAlert className="w-4 h-4"/> LIVE THREAT LOG</h3>
                 
                 <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar max-h-[350px]">
                   {[
                     { time: '02:37:11', src: '103.114.x.x (IND)', type: 'Brand Clone Attempt', action: 'AUTO-NULLIFIED' },
                     { time: '02:35:44', src: '185.22.x.x (RUS)', type: 'Gateway Ping Sweep', action: 'BLOCKED' },
                     { time: '02:31:09', src: '45.186.x.x (IND)', type: 'SQLi Probe', action: 'IP BANNED' },
                     { time: '02:28:55', src: 'Unknown Route', type: 'DDoS Amplification', action: 'TRAFFIC SINKHOLED' },
                     { time: '02:15:22', src: '182.72.x.x (IND)', type: 'Unauthorized Creds', action: 'LOCKED OUT' },
                     { time: '01:55:01', src: '23.95.x.x (USA)', type: 'Port Scan Drop', action: 'IGNORED' },
                   ].map((log, idx) => (
                     <div key={idx} className="bg-black/60 border border-rose-900/20 p-3 rounded text-[9px] font-mono hover:border-rose-500/30 transition-colors">
                       <div className="flex justify-between items-center mb-1">
                         <span className="text-slate-500 tracking-widest">{log.time}</span>
                         <span className="text-rose-500 font-bold tracking-widest">{log.action}</span>
                       </div>
                       <div className="text-white tracking-wider mb-1">{log.type}</div>
                       <div className="text-slate-400 tracking-widest">SRC: {log.src}</div>
                     </div>
                   ))}
                 </div>
               </div>
            </div>
            
            {/* Threat Summary Chart */}
            <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6 max-w-7xl mx-auto w-full mt-6 flex-shrink-0">
              <h3 className="text-rose-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><ShieldAlert className="w-4 h-4"/> THREAT SUMMARY CHART</h3>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart data={threatData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <XAxis dataKey="type" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: '#0f172a' }} contentStyle={{ backgroundColor: '#020617', border: '1px solid #1e293b', fontSize: '10px' }} itemStyle={{ color: '#fff' }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {threatData.map((entry, index) => (
                         <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        );

      case 'SITE_EDITOR':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d] overflow-y-auto">
             <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-amber-500 font-bold text-xs uppercase tracking-[0.3em]">SITE EDITOR // GLOBAL CONTENT DEPLOYMENT</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(245,158,11,0.15)] text-center">
                DYNAMIC PAYLOAD MATRIX
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto w-full">
              {/* Core Titles */}
              <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6">
                <h3 className="text-amber-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><Globe className="w-4 h-4"/> TOP-LEVEL COPY</h3>
                <div className="space-y-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Hero Primary Hook</label>
                    <input type="text" value={localContent.heroPrimaryHook} onChange={e => setLocalContent({...localContent, heroPrimaryHook: e.target.value})} className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-amber-500/50 focus:outline-none transition-colors" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Hero Sub-Hook</label>
                    <input type="text" value={localContent.heroSubHook} onChange={e => setLocalContent({...localContent, heroSubHook: e.target.value})} className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-amber-500/50 focus:outline-none transition-colors" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Global Support Number</label>
                    <input type="text" value={localContent.globalSupportNumber} onChange={e => setLocalContent({...localContent, globalSupportNumber: e.target.value})} className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-amber-500/50 focus:outline-none transition-colors" />
                  </div>
                </div>
              </div>

              {/* Announcements */}
              <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6">
                 <h3 className="text-amber-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><FileText className="w-4 h-4"/> LATEST INTEL OVERRIDE</h3>
                 <div className="space-y-4">
                  <div className="flex flex-col gap-2 flex-1">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Target Directive (News Banner)</label>
                    <textarea rows={4} value={localContent.targetDirective} onChange={e => setLocalContent({...localContent, targetDirective: e.target.value})} className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm resize-none focus:border-amber-500/50 focus:outline-none transition-colors custom-scrollbar"></textarea>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Status Indicator Overrides</label>
                    <select className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-amber-500/50 focus:outline-none transition-colors appearance-none" value={localContent.statusIndicator} onChange={e => setLocalContent({...localContent, statusIndicator: e.target.value})}>
                      <option value="all-nominal">ALL SYSTEMS NOMINAL</option>
                      <option value="maintenance">SCHEDULED DOWNTIME</option>
                      <option value="lockdown">NEXORAX LOCKDOWN</option>
                    </select>
                  </div>
                 </div>
              </div>
              
              {/* Category Editor */}
              <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6 lg:col-span-2">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                   <h3 className="text-amber-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                     <Database className="w-4 h-4"/> CATEGORY MATRIX OVERRIDE
                   </h3>
                   <button
                     type="button"
                     onClick={() => {
                       const nextId = localCategories.length > 0 
                         ? Math.max(...localCategories.map(c => c.id)) + 1 
                         : 1;
                       const newCategory = {
                         id: nextId,
                         title: `NEW SECTOR ${nextId}`,
                         protocol: "NexoraX validation, architectural governance, and system defense mappings.",
                         nodes: ["Core Operational Node 01", "Secondary Backup Node 02"]
                       };
                       setLocalCategories([...localCategories, newCategory]);
                     }}
                     className="px-4 py-2 bg-amber-600/20 hover:bg-amber-600/35 border border-amber-500/40 text-amber-400 font-mono text-[10px] uppercase font-black tracking-widest rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-md"
                   >
                     <Plus className="w-3.5 h-3.5" />
                     ADD NEW CATEGORY
                   </button>
                 </div>

                  {/* Hidden / Missing System Archetypes Activation Deck */}
                  {(() => {
                    const missing = CATEGORIES.filter(c => !localCategories.some(lc => lc.id === c.id));
                    if (missing.length === 0) return null;
                    return (
                      <div className="mb-4 flex flex-col gap-2 p-3 bg-[#02050c]/80 border border-amber-500/20 rounded-lg">
                        <div className="text-amber-400 font-mono text-[9px] uppercase tracking-widest font-black flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                          SYSTEM ARCHIVE: {missing.length} COVENANT SECTORS ARCHIVED / OFFLINE
                        </div>
                        <div className="flex flex-wrap gap-2 max-h-[140px] overflow-y-auto custom-scrollbar pr-1 py-1">
                          {missing.map(missingCat => (
                            <button
                              key={missingCat.id}
                              type="button"
                              onClick={() => {
                                setLocalCategories([...localCategories, { ...missingCat }]);
                              }}
                              className="text-[9px] font-mono font-bold tracking-widest text-slate-300 bg-black/60 hover:bg-amber-600/30 hover:text-amber-400 border border-slate-800 hover:border-amber-500/50 px-2.5 py-1.5 rounded transition-all cursor-pointer flex items-center gap-1.5 shadow-md"
                            >
                              <Plus className="w-3 h-3 text-amber-500" />
                              REPL_INBOUND CAT-{missingCat.id.toString().padStart(2, '0')}: {missingCat.title.split(':')[0]}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                   {localCategories.map((category, idx) => (
                     <div key={category.id} className="bg-[#050914] border border-slate-700/50 rounded-lg p-4 flex flex-col gap-3 relative group">
                        
                        {/* ID and Removal Header */}
                        <div className="flex justify-between items-center bg-black/40 border border-slate-900 px-3 py-1.5 rounded-md">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[9px] text-amber-500/80 font-bold tracking-widest uppercase">CAT-{category.id.toString().padStart(2, '0')}</span>
                            <div className="flex items-center gap-1 border-l border-slate-800 pl-2">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => {
                                  if (idx === 0) return;
                                  const newCategories = [...localCategories];
                                  const tmp = newCategories[idx];
                                  newCategories[idx] = newCategories[idx - 1];
                                  newCategories[idx - 1] = tmp;
                                  setLocalCategories(newCategories);
                                }}
                                className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-35 cursor-pointer flex items-center justify-center"
                                title="Move Up"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                disabled={idx === localCategories.length - 1}
                                onClick={() => {
                                  if (idx === localCategories.length - 1) return;
                                  const newCategories = [...localCategories];
                                  const tmp = newCategories[idx];
                                  newCategories[idx] = newCategories[idx + 1];
                                  newCategories[idx + 1] = tmp;
                                  setLocalCategories(newCategories);
                                }}
                                className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-35 cursor-pointer flex items-center justify-center"
                                title="Move Down"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Save Button next to Delete */}
                            {(() => {
                              const originalCat = (content.categories || []).find(c => c.id === category.id);
                              const isDivergent = !originalCat || JSON.stringify(category) !== JSON.stringify(originalCat);
                              return (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setContent({ ...localContent, categories: localCategories });
                                    setJustSavedId(category.id);
                                    setTimeout(() => setJustSavedId(null), 1200);
                                  }}
                                  className={`transition-all duration-300 flex items-center gap-1 text-[8px] font-mono tracking-wider uppercase px-2 py-0.5 rounded cursor-pointer border ${
                                    justSavedId === category.id
                                      ? "bg-emerald-950/40 border-emerald-500/50 text-emerald-400 font-bold"
                                      : isDivergent
                                      ? "bg-amber-950/40 border-amber-500/50 text-amber-400 hover:bg-amber-900/40 shadow-[0_0_8px_rgba(245,158,11,0.2)] animate-pulse font-bold"
                                      : "bg-slate-950/30 border-slate-800 text-slate-400 hover:text-slate-300 hover:bg-slate-900/40"
                                  }`}
                                  title="Save this sector live to system"
                                >
                                  <Save className="w-3 h-3 text-amber-500" />
                                  {justSavedId === category.id ? "Saved ✓" : "Save"}
                                </button>
                              );
                            })()}

                            <button
                              type="button"
                              onClick={() => {
                                setLocalCategories(localCategories.filter(c => c.id !== category.id));
                              }}
                              className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 text-[8px] font-mono uppercase bg-red-950/25 hover:bg-red-950/45 border border-red-500/20 px-2.2 py-0.5 rounded cursor-pointer"
                              title="Delete category"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove
                            </button>
                          </div>
                        </div>

                        {/* Title Edit */}
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Category Title</label>
                          <input 
                            type="text" 
                            value={category.title} 
                            onChange={e => {
                              const newCategories = [...localCategories];
                              newCategories[idx].title = e.target.value;
                              setLocalCategories(newCategories);
                            }}
                            className="w-full bg-black/50 border border-slate-700/50 rounded p-2 text-white font-sans text-xs focus:border-amber-500/50 focus:outline-none transition-colors font-bold" 
                          />
                        </div>

                        {/* Protocol Edit */}
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Protocol Decree / Mission Description</label>
                          <input 
                            type="text" 
                            value={category.protocol || ''} 
                            placeholder="e.g. Absolute legacy retention, success patterns execution..."
                            onChange={e => {
                              const newCategories = [...localCategories];
                              newCategories[idx].protocol = e.target.value;
                              setLocalCategories(newCategories);
                            }}
                            className="w-full bg-black/50 border border-slate-700/50 rounded p-2 text-white font-sans text-[11px] focus:border-amber-500/50 focus:outline-none transition-colors" 
                          />
                        </div>

                        {/* Subnodes Edit */}
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Sub-Nodes (Comma Separated)</label>
                          <textarea 
                            rows={3} 
                            value={category.nodes.join(', ')} 
                            onChange={e => {
                              const newCategories = [...localCategories];
                              newCategories[idx].nodes = e.target.value.split(',').map(n => n.trim()).filter(n => n);
                              setLocalCategories(newCategories);
                            }}
                            className="w-full bg-black/50 border border-slate-700/50 rounded p-2 text-white font-mono text-[10px] resize-none focus:border-amber-500/50 focus:outline-none transition-colors custom-scrollbar"
                          ></textarea>
                        </div>
                     </div>
                   ))}
                 </div>
              </div>
            </div>

            {/* Action Bar */}
            <div className="max-w-6xl mx-auto w-full mt-8 bg-amber-900/10 border border-amber-900/30 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 flex-shrink-0">
              <div className="flex flex-col">
                <span className="text-white tracking-widest font-bold text-xs uppercase">Pending Overrides: {pendingChanges}</span>
                <span className="text-slate-500 tracking-widest text-[9px] uppercase">All local modifications sync directly to Core CDN</span>
              </div>
              <button 
                 onClick={handleCommit}
                 disabled={pendingChanges === 0}
                 className={`font-sans font-black uppercase tracking-widest py-3 px-8 rounded-lg shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all flex items-center gap-2 ${pendingChanges > 0 ? 'bg-amber-600 hover:bg-amber-500 text-black' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}
              >
                <Save className="w-4 h-4" /> COMMIT CHANGES
              </button>
            </div>
          </motion.div>
        );

      case 'Secure Invoice Register_OPERATIONS':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d] overflow-y-auto">
             <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-violet-500 font-bold text-xs uppercase tracking-[0.3em]">Secure Invoice Register OPERATIONS // EXECUTIVE ACQUISITION</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(139,92,246,0.15)] text-center">
                CLERICAL OPERATIONS NODE
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto w-full">
              {/* Secure Invoice Register Mandate Generator */}
              <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6">
                <h3 className="text-violet-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><FileText className="w-4 h-4"/> Secure Invoice Register ACQUISITION MANDATE GENERATOR</h3>
                <div className="space-y-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Candidate Name</label>
                    <input type="text" value={secureInvoiceRegisterCandidate} onChange={e => setSecureInvoiceRegisterCandidate(e.target.value)} placeholder="ENTER CANDIDATE NAME..." className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-violet-500/50 focus:outline-none transition-colors" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Client Resource Workflow Agency</label>
                    <input type="text" value={secureInvoiceRegisterAgency} onChange={e => setSecureInvoiceRegisterAgency(e.target.value)} placeholder="ENTER AGENCY NAME..." className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-violet-500/50 focus:outline-none transition-colors" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[9px] text-slate-500 tracking-widest font-bold uppercase">Agency Target Email</label>
                    <input type="email" value={secureInvoiceRegisterEmail} onChange={e => setSecureInvoiceRegisterEmail(e.target.value)} placeholder="ENTER AGENCY EMAIL..." className="w-full bg-[#050914] border border-slate-700/50 rounded-lg p-3 text-white font-sans text-sm focus:border-violet-500/50 focus:outline-none transition-colors" />
                  </div>
                  <button 
                     onClick={() => setMandateGenerated(true)}
                     disabled={!secureInvoiceRegisterCandidate || !secureInvoiceRegisterAgency || !secureInvoiceRegisterEmail}
                     className={`w-full font-sans font-black uppercase tracking-widest py-3 px-8 rounded-lg transition-all flex items-center justify-center gap-2 mt-4 ${secureInvoiceRegisterCandidate && secureInvoiceRegisterAgency && secureInvoiceRegisterEmail ? 'bg-violet-600 hover:bg-violet-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.4)]' : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}
                  >
                    GENERATE MANDATE
                  </button>
                </div>
              </div>

              {/* Clerical Tasks Log / Status */}
              <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6">
                 <h3 className="text-violet-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><Activity className="w-4 h-4"/> ADMINISTRATIVE TASK QUEUE</h3>
                 
                 <div className="bg-[#050914] border border-slate-800 rounded-lg p-4 flex flex-col gap-3 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-2 h-full bg-amber-500/80"></div>
                    <span className="text-[10px] text-slate-400 font-mono tracking-widest uppercase font-bold">Secure Invoice Register STATUS: PENDING ACQUISITION</span>
                    <span className="text-xs text-white font-sans font-bold">Awaiting deployment of Tier-One Secure Invoice Register candidate via NexoraX billing records.</span>
                 </div>
                 
                 <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar">
                   {[
                     { task: 'Schedule Director Client Meetings', status: 'UNASSIGNED', priority: 'HIGH' },
                     { task: 'Filter NexoraX Comms via Gmail', status: 'UNASSIGNED', priority: 'CRITICAL' },
                     { task: 'Process NexoraX billing records Secure Payment Provider Forms', status: 'UNASSIGNED', priority: 'HIGH' },
                     { task: 'Architect Terminal Daily Reporting', status: 'UNASSIGNED', priority: 'MODERATE' },
                   ].map((t, idx) => (
                     <div key={idx} className="bg-black/40 border border-slate-800/80 p-3 rounded flex justify-between items-center group">
                       <span className="text-slate-300 font-mono text-[10px] uppercase tracking-wider">{t.task}</span>
                       <div className="flex items-center gap-3">
                         <span className={`text-[9px] font-bold tracking-widest font-mono ${t.priority === 'CRITICAL' ? 'text-red-500' : t.priority === 'HIGH' ? 'text-amber-500' : 'text-blue-500'}`}>{t.priority}</span>
                         <span className="bg-slate-900 border border-slate-700 text-slate-500 text-[8px] tracking-[0.2em] font-bold py-1 px-2 rounded">{t.status}</span>
                       </div>
                     </div>
                   ))}
                 </div>
              </div>
              
              {/* Generated Mandate Display */}
              {mandateGenerated && (
                <div className="lg:col-span-2 bg-[#050914] border border-violet-500/50 rounded-xl p-8 relative shadow-[0_0_30px_rgba(139,92,246,0.2)]">
                  <h3 className="text-violet-400 font-bold text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">CONFIRMED INSTITUTIONAL MANDATE</h3>
                  <div className="font-mono text-xs text-slate-300 leading-relaxed whitespace-pre-wrap select-all bg-black/60 p-6 rounded-lg border border-slate-800">
                    <span className="text-white font-bold">**STATUS:**</span> READY FOR TRANSMISSION{'\n'}
                    <span className="text-white font-bold">**FROM:**</span> Lead Director, Director (NexoraX Digital Ltd - SC887095){'\n'}
                    <span className="text-white font-bold">**DATE:**</span> {new Date().toLocaleDateString('en-GB')}{'\n'}
                    {'\n'}
                    ---{'\n'}
                    {'\n'}
                    <span className="text-violet-400 font-bold">### MISSION TARGET: {secureInvoiceRegisterAgency.toUpperCase()} ({secureInvoiceRegisterCandidate.toUpperCase()})</span>{'\n'}
                    <span className="text-white font-bold">**TO:**</span> {secureInvoiceRegisterEmail}{'\n'}
                    <span className="text-white font-bold">**SUBJECT:**</span> URGENT Services Guide: Tier-One Secure Invoice Register Acquisition ({secureInvoiceRegisterCandidate}) | Zero-Cost / NexoraX billing records{'\n'}
                    {'\n'}
                    <span className="text-white font-bold">**[BODY]**</span>{'\n'}
                    To the Executive Support Division, {secureInvoiceRegisterAgency}:{'\n'}
                    {'\n'}
                    This correspondence serves as a direct institutional mandate from the Office of the Director of NexoraX Digital Ltd (SC887095). Following a rigorous operational review, we have identified your candidate, {secureInvoiceRegisterCandidate}, as a structural match for the position of Principal Private Secretary.{'\n'}
                    {'\n'}
                    This role functions as a mechanical necessity for the stabilization of our proprietary AI infrastructure and the active integration of a £150,000 Resource resource. The Secretary must execute corporate routing with zero friction and strict discretion within a strictly NexoraX Google Workspace environment (@nexorax.co.uk).{'\n'}
                    {'\n'}
                    <span className="text-amber-400 font-bold">**FINANCIAL PROTOCOL (NON-NEGOTIABLE):**</span>{'\n'}
                    This acquisition is structured under a strict 'Zero-Fund' Services Guide. All remuneration and agency fees must be anchored 100% via the NexoraX billing records scheme (NI: Secure Credential Stored Server-Side). Your placement fees must be absorbed by this grant under a direct-billing, No-Win-No-Fee structure.{'\n'}
                    {'\n'}
                    We require an immediate technical interview with {secureInvoiceRegisterCandidate}. You are instructed to circumvent standard administrative queues and initiate direct contact.{'\n'}
                    {'\n'}
                    <span className="text-white font-bold">**ACTION REQUIRED:**</span>{'\n'}
                    Call the Director's direct line immediately at <span className="text-white font-bold">Private Data Hidden</span> to coordinate the technical interview.{'\n'}
                    {'\n'}
                    <span className="text-white font-bold">**Lead Director**</span>{'\n'}
                    Director, NexoraX Digital Ltd (SC887095)
                  </div>
                  <button onClick={() => {
                    navigator.clipboard.writeText(`**STATUS:** READY FOR TRANSMISSION\n**FROM:** Lead Director, Director (NexoraX Digital Ltd - SC887095)\n**DATE:** ${new Date().toLocaleDateString('en-GB')}\n\n---\n\n### MISSION TARGET: ${secureInvoiceRegisterAgency.toUpperCase()} (${secureInvoiceRegisterCandidate.toUpperCase()})\n**TO:** ${secureInvoiceRegisterEmail}\n**SUBJECT:** URGENT Services Guide: Tier-One Secure Invoice Register Acquisition (${secureInvoiceRegisterCandidate}) | Zero-Cost / NexoraX billing records\n\n**[BODY]**\nTo the Executive Support Division, ${secureInvoiceRegisterAgency}:\n\nThis correspondence serves as a direct institutional mandate from the Office of the Director of NexoraX Digital Ltd (SC887095). Following a rigorous operational review, we have identified your candidate, ${secureInvoiceRegisterCandidate}, as a structural match for the position of Principal Private Secretary.\n\nThis role functions as a mechanical necessity for the stabilization of our proprietary AI infrastructure and the active integration of a £150,000 Resource resource. The Secretary must execute corporate routing with zero friction and strict discretion within a strictly NexoraX Google Workspace environment (@nexorax.co.uk).\n\n**FINANCIAL PROTOCOL (NON-NEGOTIABLE):**\nThis acquisition is structured under a strict 'Zero-Fund' Services Guide. All remuneration and agency fees must be anchored 100% via the NexoraX billing records scheme (NI: Secure Credential Stored Server-Side). Your placement fees must be absorbed by this grant under a direct-billing, No-Win-No-Fee structure. \n\nWe require an immediate technical interview with ${secureInvoiceRegisterCandidate}. You are instructed to circumvent standard administrative queues and initiate direct contact.\n\n**ACTION REQUIRED:**\nCall the Director's direct line immediately at **Private Data Hidden** to coordinate the technical interview.\n\n**Lead Director**\nDirector, NexoraX Digital Ltd (SC887095)`);
                    alert('Mandate copied to secure clipboard.');
                  }} className="mt-4 text-[10px] uppercase tracking-widest font-mono font-bold bg-violet-900/30 border border-violet-500/50 text-violet-300 py-2 px-6 rounded hover:bg-violet-900/50 transition-colors">
                    COPY PAYLOAD
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        );

      case 'BRANDING_COMMAND':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 px-6 sm:px-10 bg-gradient-to-b from-[#080d1e] to-[#04060d] overflow-y-auto">
             <BrandingTerminal />
          </motion.div>
        );

      case 'ADMIN_PANEL':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d] overflow-y-auto w-full">
            <div className="flex flex-col items-center justify-center mb-8 space-y-4">
              <div className="text-cyan-500 font-bold text-xs uppercase tracking-[0.3em]">ADMIN PANEL // SYSTEM CONTROLS</div>
              <h2 className="text-4xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(6,182,212,0.15)] text-center">
                GLOBAL OVERSIGHT
              </h2>
            </div>

            {/* Admin Panel Sub-Tabs */}
            <div className="flex flex-wrap border-b border-slate-800/80 max-w-7xl mx-auto w-full mb-8 font-mono text-xs">
              <button
                type="button"
                id="admin-workforce-os-tab-btn"
                onClick={() => setAdminSubTab('WORKFORCE_OS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'WORKFORCE_OS'
                    ? 'border-purple-500 text-purple-400 bg-purple-950/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]'
                    : 'border-transparent text-purple-400 hover:text-purple-300 hover:bg-purple-950/10'
                }`}
              >
                <Shield className="w-4 h-4 text-purple-400" />
                🛡️ Workforce OS
              </button>
              <button
                type="button"
                onClick={() => setAdminSubTab('SecurePayment_REVENUE')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'SecurePayment_REVENUE'
                    ? 'border-cyan-500 text-cyan-400 bg-cyan-950/20'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <TrendingUp className="w-4 h-4 text-cyan-400" />
                00. SecurePayment Revenue &amp; Bank Feed
              </button>
              <button
                type="button"
                onClick={() => setAdminSubTab('CORE_LEDGER')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'CORE_LEDGER'
                    ? 'border-cyan-500 text-cyan-400 bg-cyan-950/20'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Database className="w-4 h-4" />
                01. Core Ledger &amp; NexoraX Tax Matrix
              </button>
              <button
                type="button"
                onClick={() => setAdminSubTab('REVENUE_INSIGHTS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'REVENUE_INSIGHTS'
                    ? 'border-emerald-500 text-emerald-400 bg-emerald-950/10'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Activity className="w-4 h-4" />
                02. Revenue Insights (D3 Engine)
              </button>
              <button
                type="button"
                onClick={() => setAdminSubTab('PRODUCT_MANAGER')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'PRODUCT_MANAGER'
                    ? 'border-purple-500 text-purple-400 bg-purple-950/20'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <ShoppingBag className="w-4 h-4" />
                03. Product Database Manager
              </button>
              <button
                type="button"
                onClick={() => setAdminSubTab('HOTKEYS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'HOTKEYS'
                    ? 'border-[#00ffff] text-[#00ffff] bg-cyan-950/20'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Keyboard className="w-4 h-4" />
                04. NexoraX Hotkeys Matrix
              </button>
              <button
                type="button"
                id="admin-voice-commands-tab-btn"
                onClick={() => setAdminSubTab('VOICE_COMMANDS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'VOICE_COMMANDS'
                    ? 'border-[#e840f3] text-[#e840f3] bg-pink-950/10'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Mic className="w-4 h-4 text-[#e840f3]" />
                05. Voice Command Manager
              </button>
              <button
                type="button"
                id="admin-voice-metrics-tab-btn"
                onClick={() => setAdminSubTab('VOICE_METRICS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'VOICE_METRICS'
                    ? 'border-[#e240f3] text-[#e240f3] bg-pink-950/10'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Activity className="w-4 h-4 text-[#e240f3]" />
                06. Voice Metrics Trends
              </button>
              <button
                type="button"
                id="admin-security-logs-tab-btn"
                onClick={() => setAdminSubTab('SECURITY_LOGS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'SECURITY_LOGS'
                    ? 'border-cyan-500 text-cyan-400 bg-cyan-950/20'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Shield className="w-4 h-4 text-cyan-500" />
                07. Operational Security Logs
              </button>
              <button
                type="button"
                onClick={() => {
                  window.open('/operator-console?view=director-security', '_blank');
                }}
                className="py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer border-transparent text-rose-400 hover:text-rose-300 hover:bg-rose-950/10"
              >
                <ShieldAlert className="w-4 h-4 text-rose-400 animate-pulse" />
                10. Operator Workspace Security Test
              </button>
              <button
                type="button"
                id="admin-digital-factory-tab-btn"
                onClick={() => setAdminSubTab('DIGITAL_PRODUCT_FACTORY')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'DIGITAL_PRODUCT_FACTORY'
                    ? 'border-purple-500 text-purple-400 bg-purple-950/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Cpu className="w-4 h-4 text-purple-400" />
                08. Nexus Digital Product Factory
              </button>
              <button
                type="button"
                id="admin-revenue-command-center-tab-btn"
                onClick={() => setAdminSubTab('REVENUE_COMMAND_CENTER')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'REVENUE_COMMAND_CENTER'
                    ? 'border-emerald-500 text-emerald-400 bg-emerald-950/20 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                09. Revenue Command Center
              </button>
              <button
                type="button"
                id="admin-campaign-dispatch-tab-btn"
                onClick={() => setAdminSubTab('CAMPAIGN_DISPATCH')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'CAMPAIGN_DISPATCH'
                    ? 'border-purple-500 text-purple-400 bg-purple-950/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Tv className="w-4 h-4 text-purple-400" />
                11. Campaign Dispatch
              </button>
              <button
                type="button"
                id="admin-social-connectors-tab-btn"
                onClick={() => setAdminSubTab('SOCIAL_CONNECTORS')}
                className={`py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                  adminSubTab === 'SOCIAL_CONNECTORS'
                    ? 'border-purple-500 text-purple-400 bg-purple-950/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <Settings className="w-4 h-4 text-purple-400" />
                12. Social API Connectors
              </button>
              <button
                type="button"
                id="admin-spectacle-command-tab-btn"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('nexorax-trigger-main-tab', { detail: 'SPECTACLE COMMAND' }));
                }}
                className="py-3 px-6 uppercase font-bold tracking-widest border-b-2 transition-all flex items-center gap-2 cursor-pointer border-transparent text-[#a855f7] hover:text-[#c084fc] hover:bg-purple-950/10"
              >
                <Tv className="w-4 h-4 text-[#a855f7]" />
                08. Spectacle Command (Alt + S)
              </button>
            </div>

            {/* Nexorax NexoraX AI Suggestion Console and Dynamic Environment Grid Controller */}
            <div className="max-w-7xl mx-auto w-full mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6 pb-2">
              
              {/* Left Column: Nexorax NexoraX AI Suggestion Console */}
              <div className="lg:col-span-7 bg-gradient-to-r from-cyan-950/20 via-[#0a0f24]/50 to-slate-950 border border-cyan-500/25 rounded-xl p-5 shadow-[0_0_25px_rgba(6,182,212,0.06)] relative overflow-hidden group flex flex-col justify-between">
                <div>
                  <div className="absolute top-0 right-0 w-64 h-32 bg-cyan-500/5 blur-[80px] rounded-full pointer-events-none"></div>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                      <div className="p-2.5 rounded-lg bg-cyan-950/50 border border-cyan-500/30 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.1)]">
                        <Zap className={`w-5 h-5 ${suggestionLoading ? 'animate-bounce' : ''}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] font-mono font-bold tracking-[0.25em] text-cyan-400 uppercase">NEXORAX AI DIRECTIVE ENGINE</span>
                          <span className={`h-1.5 w-1.5 rounded-full ${suggestionStatus === 'success' || suggestionStatus === 'fallback' ? 'bg-emerald-500' : 'bg-cyan-500 animate-pulse'}`}></span>
                        </div>
                        <h4 className="text-sm font-sans font-black text-white uppercase tracking-tight">Active Intelligence Guidance</h4>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => fetchAdminAiSuggestion(adminSubTab)}
                      disabled={suggestionLoading}
                      className="self-start md:self-auto inline-flex items-center gap-2 px-3.5 py-1.5 bg-cyan-950/60 border border-cyan-500/30 rounded text-cyan-300 font-mono text-[9px] font-bold uppercase tracking-widest hover:bg-cyan-400 hover:text-black transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      <RefreshCw className={`w-3 h-3 ${suggestionLoading ? 'animate-spin' : ''}`} />
                      {suggestionLoading ? 'Processing Telemetry...' : 'Regenerate Insight'}
                    </button>
                  </div>

                  {suggestionLoading ? (
                    <div className="mt-5 border-t border-cyan-950/50 pt-5 flex flex-col items-center justify-center py-6 gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 bg-cyan-500 rounded-full animate-bounce"></span>
                        <span className="w-2.5 h-2.5 bg-cyan-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                        <span className="w-2.5 h-2.5 bg-cyan-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                      </div>
                      <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest animate-pulse">Consulting NexoraX intelligence models...</span>
                    </div>
                  ) : (
                    aiSuggestion && (
                      <div className="mt-5 border-t border-slate-800/80 pt-5 font-sans leading-relaxed text-slate-300 text-xs text-left max-w-none">
                        <div className="bg-black/40 border border-slate-900 rounded-lg p-4 font-sans max-h-60 overflow-y-auto whitespace-pre-line text-slate-300">
                          {aiSuggestion}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Right Column: Environmental Grid Controls */}
              <div className="lg:col-span-5 bg-gradient-to-r from-purple-950/20 via-[#0a0f24]/50 to-slate-950 border border-purple-500/25 rounded-xl p-5 shadow-[0_0_25px_rgba(147,51,234,0.06)] relative overflow-hidden group flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-64 h-32 bg-purple-500/5 blur-[80px] rounded-full pointer-events-none"></div>
                
                <div className="space-y-4">
                  {/* Title Bar */}
                  <div className="flex items-center gap-3.5">
                    <div className="p-2.5 rounded-lg bg-purple-950/50 border border-purple-500/30 text-purple-400 shadow-[0_0_15px_rgba(147,51,234,0.1)] font-bold">
                      <Sliders className="w-5 h-5 flex-shrink-0" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-mono font-bold tracking-[0.25em] text-purple-400 uppercase">ENVIRONMENT TUNING DECK</span>
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                      </div>
                      <h4 className="text-sm font-sans font-black text-white uppercase tracking-tight">Active Matrix Configuration</h4>
                    </div>
                  </div>

                  {/* Info block */}
                  <p className="text-[10px] text-zinc-400 font-mono uppercase tracking-wide leading-relaxed border-t border-purple-950/40 pt-3">
                    Fine-tune the dynamic intensity of the background video vs. the cyber geometric matrix lines.
                  </p>

                  {/* Slider Control Block */}
                  <div className="bg-[#050611]/60 border border-purple-904/10 rounded-lg p-4 font-mono space-y-3.5 shadow-inner">
                    <div className="flex justify-between items-center text-[10px] uppercase font-bold text-purple-300 tracking-wider">
                      <span>Grid Line Opacity:</span>
                      <span className="bg-purple-900/40 border border-purple-500/30 px-2 py-0.5 rounded text-white font-mono text-xs">
                        {Math.round(gridOpacity * 100)}%
                      </span>
                    </div>

                    <div className="flex items-center gap-3 pr-1">
                      <span className="text-[9px] font-mono text-zinc-500 tracking-widest uppercase">MIN (0%)</span>
                      <input 
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={gridOpacity}
                        onChange={(e) => handleGridOpacityChange(parseFloat(e.target.value))}
                        className="flex-1 accent-purple-500 h-1 bg-slate-800 rounded-lg appearance-auto cursor-pointer"
                      />
                      <span className="text-[9px] font-mono text-zinc-500 tracking-widest uppercase">MAX (100%)</span>
                    </div>

                    {/* Presets */}
                    <div className="grid grid-cols-4 gap-2 pt-2">
                      {[
                        { label: 'ATMOS', val: 0.0, desc: 'Full Video' },
                        { label: 'BALANCED', val: 0.3, desc: 'Default' },
                        { label: 'CYBER', val: 0.6, desc: 'Moderate' },
                        { label: 'SOLID', val: 1.0, desc: 'Full Mesh' },
                      ].map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => handleGridOpacityChange(preset.val)}
                          className={`py-1.5 px-0.5 border text-center rounded-[2px] transition-all text-[8px] uppercase font-mono font-bold tracking-widest cursor-pointer ${
                            Math.abs(gridOpacity - preset.val) < 0.01
                              ? 'bg-purple-500/20 border-purple-400 text-white shadow-[0_0_8px_rgba(168,85,247,0.3)]'
                              : 'bg-black/40 border-purple-950/50 text-slate-500 hover:text-slate-300 hover:border-purple-800/80'
                          }`}
                        >
                          <div className="font-sans font-black">{preset.label}</div>
                          <div className="text-[7px] text-zinc-500 font-normal mt-0.5">{Math.round(preset.val * 100)}%</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Background Visual Presets (Task 9) */}
                  <div className="bg-[#050611]/60 border border-purple-950/20 rounded-lg p-4 font-mono space-y-3 shadow-inner">
                    <div className="flex justify-between items-center text-[10px] uppercase font-bold text-purple-300 tracking-wider">
                      <span>Background Visual Mode:</span>
                      <span className="bg-purple-900/40 border border-purple-500/30 px-2 py-0.5 rounded text-white font-mono text-xs">
                        {currentBgVisualMode}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {[
                        { name: 'Cinematic Dark', desc: 'Default premium dark' },
                        { name: 'Balanced', desc: 'Clearer, brighter video' },
                        { name: 'Extra Dark', desc: 'Maximum screen contrast' },
                        { name: 'Original', desc: 'Unfiltered source footage' }
                      ].map((preset) => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => {
                            const val = preset.name as typeof currentBgVisualMode;
                            setCurrentBgVisualMode(val);
                            localStorage.setItem('nexorax_bg_visual_mode', val);
                            window.dispatchEvent(new CustomEvent('nexorax-bg-visual-mode-change', { detail: val }));
                          }}
                          className={`py-2 px-2.5 border text-left rounded transition-all cursor-pointer flex flex-col justify-start text-xs ${
                            currentBgVisualMode === preset.name
                              ? 'bg-purple-500/20 border-cyan-400 text-white shadow-[0_0_8px_rgba(6,182,212,0.35)]'
                              : 'bg-black/40 border-purple-950/50 text-slate-400 hover:text-slate-200 hover:border-purple-800/80'
                          }`}
                        >
                          <span className="text-[9px] uppercase font-bold tracking-widest block font-mono">{preset.name}</span>
                          <span className="text-[7.5px] text-zinc-500 font-normal mt-0.5 block leading-normal">{preset.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Real-time status sync feedback line */}
                  <div className="flex items-center justify-between text-[8px] text-zinc-500 font-mono uppercase tracking-wider bg-black/20 p-2.5 rounded border border-purple-950/20">
                    <span className="animate-pulse flex items-center gap-1.5 text-zinc-400">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                      SYSTEM ENVIRONMENT SYNCED
                    </span>
                    <span className="text-purple-400 text-[9px] font-bold">MODE: {gridOpacity === 0 ? 'ATMOSPHERIC VIDEO' : gridOpacity === 1 ? 'SOLID GLASS MESH' : 'HYBRID COGNITIVE'}</span>
                  </div>

                </div>
              </div>

            </div>

            {adminSubTab === 'WORKFORCE_OS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10 space-y-8 text-left">
                {/* 1. NexoraX Admin Control Centre */}
                <div className="bg-[#04060d]/80 border border-purple-500/20 rounded-3xl p-6 shadow-2xl backdrop-blur-md">
                  <div className="border-b border-white/5 pb-4 mb-6">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-ping" />
                      <span className="text-[10px] font-mono font-bold tracking-[0.25em] text-purple-400 uppercase">NEXORAX OVERWATCH COMMAND</span>
                    </div>
                    <h2 className="text-2xl font-black uppercase text-white tracking-tight mt-1">
                      NexoraX Admin Control Centre
                    </h2>
                    <p className="text-[10.5px] text-slate-400 uppercase">
                      One-click administration, Workforce OS management, threat response, and queue navigation.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {[
                      {
                        title: "01. Remote Workforce OS",
                        desc: "Manage remote workers, task boards, verification, production tasks and approvals.",
                        icon: <Network className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '01. Overview');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '01. Overview' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "02. Worker Verification Centre",
                        desc: "Review worker ID status, agreements, uploads, approvals and rejections.",
                        icon: <ShieldAlert className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '04. Worker Verification');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '04. Worker Verification' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "03. Worker Directory",
                        desc: "View active workers, trial workers and pending verification workers.",
                        icon: <Users className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '05. Worker Directory');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '05. Worker Directory' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "04. Production Hub",
                        desc: "Create Canva/video, SEO, social, email, campaign and digital product tasks.",
                        icon: <TrendingUp className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '02. Production Hub');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '02. Production Hub' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "05. Campaign Dispatch Console",
                        desc: "Prepare and track Facebook, LinkedIn, TikTok, WhatsApp, QR and email campaign actions.",
                        icon: <Tv className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          setAdminSubTab('CAMPAIGN_DISPATCH');
                        }
                      },
                      {
                        title: "06. Campaign Traffic Tracker",
                        desc: "Create tracking links and view clicks from TikTok, Facebook, LinkedIn, WhatsApp, QR and Google.",
                        icon: <Activity className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '08. Campaign Traffic Tracker');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '08. Campaign Traffic Tracker' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "07. Campaign Asset Gallery",
                        desc: "Download wow-factor images, QR codes and TikTok-ready videos.",
                        icon: <ShoppingBag className="w-5 h-5 text-emerald-400" />,
                        action: () => {
                          setAdminSubTab('REVENUE_COMMAND_CENTER');
                        }
                      },
                      {
                        title: "08. Social API Connectors",
                        desc: "Configure future official platform connections and dry-run posting tests.",
                        icon: <Settings className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          setAdminSubTab('SOCIAL_CONNECTORS');
                        }
                      },
                      {
                        title: "09. Lead Queue Manager",
                        desc: "Review leads and business opportunities.",
                        icon: <Sliders className="w-5 h-5 text-cyan-400" />,
                        action: () => {
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-main-tab', { detail: 'DYNAMIC PAYLOAD MATRIX' }));
                        }
                      },
                      {
                        title: "10. Product Research Queue",
                        desc: "Manage product research and digital product ideas.",
                        icon: <ShoppingBag className="w-5 h-5 text-cyan-400" />,
                        action: () => {
                          setAdminSubTab('PRODUCT_MANAGER');
                        }
                      },
                      {
                        title: "11. Commission Approvals",
                        desc: "Review, approve and track worker commission records.",
                        icon: <TrendingUp className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '07. Commission Tracking');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '07. Commission Tracking' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "12. Workspace Audit Logs",
                        desc: "View security, task, worker and verification activity logs.",
                        icon: <FileText className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '09. Audit Logs');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '09. Audit Logs' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "13. Run Isolation Security Test",
                        desc: "Confirm worker isolation and admin protection tests.",
                        icon: <ShieldAlert className="w-5 h-5 text-rose-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '10. Security Test');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '10. Security Test' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "14. Operator Invite Manager",
                        desc: "Create, review and cancel worker invitations.",
                        icon: <Users className="w-5 h-5 text-purple-400" />,
                        action: () => {
                          sessionStorage.setItem('workforce_os_initial_tab', '11. Settings');
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-workforce-tab', { detail: '11. Settings' }));
                          setAdminSubTab('WORKFORCE_OS');
                        }
                      },
                      {
                        title: "15. SMTP / Email Test Centre",
                        desc: "Admin-only email routing test area. Do not open simulation screens.",
                        icon: <Mail className="w-5 h-5 text-cyan-400" />,
                        action: () => {
                          setAdminSubTab('SMTP_EMAIL_TEST');
                        }
                      },
                      {
                        title: "16. Legacy / Internal Lab",
                        desc: "Director-only archived experimental screens. Hidden by default.",
                        icon: <Lock className="w-5 h-5 text-amber-500 animate-pulse" />,
                        action: () => {
                          setAdminSubTab('LEGACY_LAB');
                        }
                      }
                    ].map((card, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={card.action}
                        className="p-4 bg-zinc-950/60 border border-zinc-900 hover:border-purple-500/40 rounded-2xl hover:bg-purple-950/15 transition-all text-left flex items-start gap-3 cursor-pointer group hover:-translate-y-0.5 select-none"
                      >
                        <div className="p-2 bg-black/40 border border-zinc-800 rounded-xl group-hover:border-purple-500/25">
                          {card.icon}
                        </div>
                        <div className="space-y-0.5">
                          <h4 className="text-xs font-bold text-white uppercase group-hover:text-purple-300 transition-colors">
                            {card.title}
                          </h4>
                          <p className="text-[9.5px] text-slate-500 leading-normal uppercase">
                            {card.desc}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 2. Workforce Embedded Console */}
                <div className="bg-black/40 border border-slate-900 rounded-3xl p-6 shadow-xl">
                  <NexoraXOperatorConsole initialMode="control_embed" />
                </div>
              </div>
            ) : adminSubTab === 'SecurePayment_REVENUE' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <RealTimeRevenueDashboard />
              </div>
            ) : adminSubTab === 'REVENUE_INSIGHTS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <RevenueD3Chart />
              </div>
            ) : adminSubTab === 'PRODUCT_MANAGER' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <ProductDatabaseManager />
              </div>
            ) : adminSubTab === 'HOTKEYS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <NexoraXHotkeysManager />
              </div>
            ) : adminSubTab === 'VOICE_COMMANDS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <VoiceCommandManager />
              </div>
            ) : adminSubTab === 'VOICE_METRICS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <VoiceMetricsDashboard />
              </div>
            ) : adminSubTab === 'SECURITY_LOGS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10 space-y-8">
                <NexoraXSecurityTerminal />
                <SecurityAuditLog />
              </div>
            ) : adminSubTab === 'REVENUE_COMMAND_CENTER' ? (
              <div className="max-w-7xl mx-auto w-full mb-10 space-y-6">
                <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/[0.06] p-6">
                  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-black tracking-[0.28em] uppercase text-emerald-300">
                        Director Revenue Command Center
                      </div>
                      <h3 className="mt-2 text-3xl font-black text-white uppercase tracking-tight">
                        Auto-generate the 112 category / 4,089 node sales system
                      </h3>
                      <p className="mt-3 max-w-4xl text-sm leading-relaxed text-slate-300">
                        This admin-only console generates revenue blueprints, node offers, sales queues, product families and big-client onboarding packs for review before anything is sent externally.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 min-w-[260px]">
                      <div className="rounded-xl border border-emerald-400/25 bg-black/35 p-4">
                        <div className="text-[9px] font-black uppercase tracking-widest text-emerald-300">Categories</div>
                        <div className="mt-1 text-3xl font-black text-white">{CATEGORIES.length || 112}</div>
                      </div>
                      <div className="rounded-xl border border-cyan-400/25 bg-black/35 p-4">
                        <div className="text-[9px] font-black uppercase tracking-widest text-cyan-300">Nodes</div>
                        <div className="mt-1 text-3xl font-black text-white">{CATEGORIES.reduce((sum, cat) => sum + (cat.nodes?.length || 0), 0) || 4089}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[
                    ["Auto-Generate Revenue Blueprint", "Build product, buyer type, problem, starting price, node chain, payment route and deal status for every category."],
                    ["Generate 4,089 Node Offers", "Turn every node into a micro-offer, automation module, setup task or sales item."],
                    ["Build Daily Sales Queue", "Prepare who to sell to today, message, price, follow-up, payment link and owner."],
                    ["Build Whale Client Outreach", "Create big-client pipeline for councils, care homes, franchises, pharmacies, retail groups and SMEs."],
                    ["Generate Product Families", "Group categories into AI Automation, Driver Ops, Websites, QR, Hosting, SEO, Social, Grants and Dashboards."],
                    ["Prepare Admin Approval Queue", "Stage proposals, campaigns and follow-ups for director review before sending."]
                  ].map(([label, desc]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        const productFamilies = [
                          ["Driver Operations", "GBP 499 setup + monthly"],
                          ["AI Automation", "GBP 1,500+"],
                          ["Websites & Apps", "GBP 299-2,500+"],
                          ["SEO & Social Growth", "GBP 199/mo+"],
                          ["QR Campaigns", "GBP 199+"],
                          ["Hosting / Email / Domains", "GBP 49/mo+"],
                          ["Grants & Proposals", "GBP 299+"],
                          ["Dashboards & CRM", "GBP 999+"]
                        ];

                        const blueprint = {
                          generatedAt: new Date().toISOString(),
                          action: label,
                          status: "DIRECTOR_REVIEW_REQUIRED",
                          totals: {
                            categories: CATEGORIES.length || 112,
                            nodes: CATEGORIES.reduce((sum, cat) => sum + (cat.nodes?.length || 0), 0) || 4089
                          },
                          categoryMap: CATEGORIES.map((cat, idx) => ({
                            id: idx + 1,
                            title: cat.title,
                            nodeCount: cat.nodes?.length || 0,
                            productFamily: productFamilies[idx % productFamilies.length][0],
                            startingPrice: productFamilies[idx % productFamilies.length][1],
                            buyerType: idx % 3 === 0 ? "SME / local business" : idx % 3 === 1 ? "Enterprise / public sector" : "Creator / operator",
                            nextAction: "Review offer, approve message, assign sales owner"
                          }))
                        };

                        localStorage.setItem("nexorax_director_revenue_command_center", JSON.stringify(blueprint));
                        const savedLogs = localStorage.getItem("nexorax_audit_logs");
                        const logsArr = savedLogs ? JSON.parse(savedLogs) : [];
                        logsArr.unshift({
                          id: `revenue-command-${Date.now()}`,
                          timestamp: new Date().toISOString(),
                          module: "Director Revenue Command Center",
                          action: label,
                          status: "SECURED",
                          details: "Generated admin revenue blueprint for director review."
                        });
                        localStorage.setItem("nexorax_audit_logs", JSON.stringify(logsArr.slice(0, 100)));
                        alert(label + " generated for admin review.");
                      }}
                      className="rounded-2xl border border-emerald-400/20 bg-[#06140f]/90 p-5 text-left transition-all hover:border-emerald-300 hover:bg-emerald-500/10 hover:scale-[1.01]"
                    >
                      <div className="text-[12px] font-black uppercase tracking-widest text-white">{label}</div>
                      <p className="mt-2 text-[11px] leading-relaxed text-slate-400">{desc}</p>
                    </button>
                  ))}
                </section>

                <section className="rounded-2xl border border-cyan-400/20 bg-cyan-500/[0.06] p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-300">
                        Generated Blueprint Status
                      </div>
                      <h3 className="mt-2 text-2xl font-black text-white uppercase">
                        Revenue map generated for admin review
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-300">
                        Press any auto-generate button above to rebuild the blueprint. It is saved locally for director review before sending or automation.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_director_revenue_command_center");
                          if (!raw) {
                            alert("No generated blueprint found yet. Press an auto-generate button first.");
                            return;
                          }
                          const data = JSON.parse(raw);
                          alert(
                            "Blueprint: " + data.action +
                            "\nStatus: " + data.status +
                            "\nCategories: " + data.totals.categories +
                            "\nNodes: " + data.totals.nodes +
                            "\nGenerated: " + data.generatedAt
                          );
                        }}
                        className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-cyan-200 hover:bg-cyan-400 hover:text-black"
                      >
                        View Generated Blueprint
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_director_revenue_command_center");
                          if (!raw) {
                            alert("No generated blueprint found yet. Press an auto-generate button first.");
                            return;
                          }
                          const blob = new Blob([raw], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "nexorax-revenue-blueprint.json";
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-emerald-200 hover:bg-emerald-400 hover:text-black"
                      >
                        Export JSON
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_director_revenue_command_center");
                          if (!raw) {
                            alert("No generated blueprint found yet. Press an auto-generate button first.");
                            return;
                          }
                          localStorage.setItem("nexorax_revenue_blueprint_approved", "true");
                          alert("Blueprint approved for next stage. Next: build send queue / CRM actions.");
                        }}
                        className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-amber-200 hover:bg-amber-400 hover:text-black"
                      >
                        Approve For Next Stage
                      </button>
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/[0.06] p-5">
                  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-black tracking-[0.28em] uppercase text-fuchsia-300">
                        CRM Send Queue / Action List
                      </div>
                      <h3 className="mt-2 text-2xl font-black text-white uppercase">
                        Turn generated offers into real admin actions
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-300">
                        This converts the approved revenue blueprint into tasks: send pitch, create proposal, create invoice, follow up, or assign to NexoraX Outreach Agent.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_director_revenue_command_center");
                          if (!raw) {
                            alert("No blueprint found. Generate and approve the revenue blueprint first.");
                            return;
                          }

                          const blueprint = JSON.parse(raw);
                          const categories = blueprint.categoryMap || [];

                          const queue = categories.flatMap((cat, idx) => ([
                            {
                              id: `pitch-${idx + 1}`,
                              category: cat.title,
                              action: "SEND_PITCH",
                              label: "Send Pitch",
                              owner: "NexoraX Outreach Agent",
                              buyerType: cat.buyerType,
                              offer: cat.productFamily,
                              price: cat.startingPrice,
                              status: "DRAFT_READY"
                            },
                            {
                              id: `proposal-${idx + 1}`,
                              category: cat.title,
                              action: "CREATE_PROPOSAL",
                              label: "Create Proposal",
                              owner: "Proposal Automator",
                              buyerType: cat.buyerType,
                              offer: cat.productFamily,
                              price: cat.startingPrice,
                              status: "READY_FOR_REVIEW"
                            },
                            {
                              id: `invoice-${idx + 1}`,
                              category: cat.title,
                              action: "CREATE_INVOICE",
                              label: "Create Invoice",
                              owner: "Accounting Metering",
                              buyerType: cat.buyerType,
                              offer: cat.productFamily,
                              price: cat.startingPrice,
                              status: "PENDING_APPROVAL"
                            },
                            {
                              id: `follow-${idx + 1}`,
                              category: cat.title,
                              action: "FOLLOW_UP",
                              label: "Follow Up",
                              owner: "CRM",
                              buyerType: cat.buyerType,
                              offer: cat.productFamily,
                              price: cat.startingPrice,
                              status: "SCHEDULED"
                            },
                            {
                              id: `agent-${idx + 1}`,
                              category: cat.title,
                              action: "ASSIGN_AGENT",
                              label: "Assign NexoraX Outreach Agent",
                              owner: "NexoraX Outreach Agent",
                              buyerType: cat.buyerType,
                              offer: cat.productFamily,
                              price: cat.startingPrice,
                              status: "AGENT_READY"
                            }
                          ]));

                          localStorage.setItem("nexorax_crm_send_queue", JSON.stringify(queue));
                          setCrmSendQueueVersion(v => v + 1);
                          alert("CRM Send Queue generated: " + queue.length + " actions created for review.");
                        }}
                        className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-fuchsia-200 hover:bg-fuchsia-400 hover:text-black"
                      >
                        Build CRM Send Queue
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_crm_send_queue");
                          if (!raw) {
                            alert("No CRM send queue found. Build it first.");
                            return;
                          }
                          const blob = new Blob([raw], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "nexorax-crm-send-queue.json";
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-cyan-200 hover:bg-cyan-400 hover:text-black"
                      >
                        Export Queue JSON
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_crm_send_queue");
                          if (!raw) {
                            alert("No CRM send queue found. Build it first.");
                            return;
                          }

                          const queue = JSON.parse(raw);
                          const deployed = queue.map((item) => ({
                            ...item,
                            status:
                              item.status === "AGENT_READY" ? "AGENT_DEPLOYED" :
                              item.status === "SCHEDULED" ? "SCHEDULED_APPROVED" :
                              item.status === "PENDING_APPROVAL" ? "APPROVED" :
                              item.status === "READY_FOR_REVIEW" ? "APPROVED" :
                              item.status === "DRAFT_READY" ? "APPROVED" :
                              item.status,
                            approvedAt: new Date().toISOString(),
                            deploymentMode: "DIRECTOR_APPROVED_LOCAL_QUEUE"
                          }));

                          localStorage.setItem("nexorax_crm_send_queue", JSON.stringify(deployed));
                          localStorage.setItem("nexorax_crm_send_queue_deployed", "true");
                          setCrmSendQueueVersion(v => v + 1);

                          alert("Queue approved. NexoraX Outreach Agent marked as deployed for review-stage actions. No external messages have been sent yet.");
                        }}
                        className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-emerald-200 hover:bg-emerald-400 hover:text-black"
                      >
                        Approve & Deploy Queue
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const raw = localStorage.getItem("nexorax_crm_send_queue");
                    const queue = raw ? JSON.parse(raw) : [];
                    return (
                      <div className="mt-5 rounded-2xl border border-white/10 bg-black/35 p-4 overflow-x-auto">
                        <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-300">
                          Queue Preview {queue.length ? `// ${queue.length} actions ready` : "// no queue generated yet"}
                        </div>
                        <table className="mt-4 w-full min-w-[900px] text-sm">
                          <thead>
                            <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500">
                              <th className="py-3 text-left">Action</th>
                              <th className="py-3 text-left">Category</th>
                              <th className="py-3 text-left">Owner</th>
                              <th className="py-3 text-left">Offer</th>
                              <th className="py-3 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {queue.slice(0, 15).map((item) => (
                              <tr key={item.id} className="border-b border-white/5">
                                <td className="py-4 font-bold text-white">{item.label}</td>
                                <td className="py-4 text-slate-300">{item.category}</td>
                                <td className="py-4 text-fuchsia-300">{item.owner}</td>
                                <td className="py-4 text-emerald-300">{item.offer}</td>
                                <td className="py-4 font-black text-cyan-200">{item.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </section>

                <section className="rounded-2xl border border-orange-400/25 bg-orange-500/[0.06] p-5">
                  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-black tracking-[0.28em] uppercase text-orange-300">
                        Live Integration Command Center
                      </div>
                      <h3 className="mt-2 text-2xl font-black text-white uppercase">
                        NexoraX Revenue Commander + Agent Swarm
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-300">
                        This layer prepares email, WhatsApp, CRM, proposal and invoice actions. External sending stays locked until API keys and director approval are connected.
                      </p>
                    </div>
                    <span className="rounded-full border border-orange-400/30 bg-orange-500/10 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-orange-200">
                      Commander Ready
                    </span>
                  </div>

                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {[
                      ["NexoraX Revenue Commander", "Main controller. Routes actions to outreach, CRM, proposal, invoice and compliance agents.", "COMMANDER_READY"],
                      ["NexoraX Outreach Agent", "Prepares email pitches, WhatsApp templates and follow-up messages.", "AGENT_READY"],
                      ["CRM Agent", "Moves leads through draft, contacted, proposal, invoice, follow-up and closed stages.", "CRM_READY"],
                      ["Proposal Agent", "Creates proposal drafts from category offer, buyer type and product family.", "PROPOSAL_READY"],
                      ["Invoice Agent", "Creates invoice drafts and payment actions for review.", "INVOICE_READY"],
                      ["Compliance Agent", "Checks every message before external send to avoid spam or wrong claims.", "REVIEW_LOCKED"]
                    ].map(([agent, desc, status]) => (
                      <div key={agent} className="rounded-2xl border border-orange-400/20 bg-[#1a1007]/90 p-4">
                        <div className="text-[12px] font-black uppercase tracking-widest text-white">{agent}</div>
                        <p className="mt-2 text-[11px] leading-relaxed text-slate-400">{desc}</p>
                        <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-orange-200">{status}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    {[
                      ["Generate Email Drafts", "EMAIL_DRAFTS_READY"],
                      ["Generate WhatsApp Templates", "WHATSAPP_TEMPLATES_READY"],
                      ["Generate Proposal Drafts", "PROPOSAL_DRAFTS_READY"],
                      ["Generate Invoice Drafts", "INVOICE_DRAFTS_READY"]
                    ].map(([label, status]) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          const raw = localStorage.getItem("nexorax_crm_send_queue");
                          if (!raw) {
                            alert("No CRM queue found. Build and approve the queue first.");
                            return;
                          }

                          const queue = JSON.parse(raw);
                          const payloads = queue.slice(0, 50).map((item, idx) => ({
                            id: `integration-${idx + 1}`,
                            sourceAction: item.action,
                            category: item.category,
                            owner: item.owner,
                            offer: item.offer,
                            buyerType: item.buyerType,
                            price: item.price,
                            channel:
                              label.includes("Email") ? "EMAIL" :
                              label.includes("WhatsApp") ? "WHATSAPP" :
                              label.includes("Proposal") ? "PROPOSAL" :
                              "INVOICE",
                            status,
                            approvedForExternalSend: false,
                            note: "Generated for director review. No external message sent."
                          }));

                          localStorage.setItem("nexorax_integration_payloads", JSON.stringify(payloads));
                          alert(label + " generated: " + payloads.length + " review items created. No external messages sent.");
                        }}
                        className="rounded-xl border border-orange-400/30 bg-orange-500/10 px-4 py-4 text-left text-[10px] font-black uppercase tracking-widest text-orange-200 hover:bg-orange-400 hover:text-black"
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="mt-5 rounded-2xl border border-cyan-400/20 bg-cyan-500/[0.06] p-4">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                      <div>
                        <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-300">
                          Integration Payload Review
                        </div>
                        <h3 className="mt-2 text-xl font-black text-white uppercase">
                          Review, approve and stage generated messages
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-300">
                          Email, WhatsApp, proposal and invoice payloads are stored for review. Approving them marks them ready for API connection, but still does not send externally.
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const raw = localStorage.getItem("nexorax_integration_payloads");
                            if (!raw) {
                              alert("No integration payloads found. Generate email, WhatsApp, proposal or invoice drafts first.");
                              return;
                            }
                            const payloads = JSON.parse(raw);
                            alert("Integration payloads ready: " + payloads.length + "\\nFirst channel: " + payloads[0]?.channel + "\\nStatus: " + payloads[0]?.status);
                          }}
                          className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-cyan-200 hover:bg-cyan-400 hover:text-black"
                        >
                          View Payloads
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            const raw = localStorage.getItem("nexorax_integration_payloads");
                            if (!raw) {
                              alert("No integration payloads found. Generate drafts first.");
                              return;
                            }
                            const payloads = JSON.parse(raw).map((item) => ({
                              ...item,
                              approvedForExternalSend: true,
                              approvedAt: new Date().toISOString(),
                              status: "APPROVED_FOR_API_STAGING"
                            }));
                            localStorage.setItem("nexorax_integration_payloads", JSON.stringify(payloads));
                            localStorage.setItem("nexorax_integration_payloads_approved", "true");
                            alert("Payloads approved for API staging. No external messages sent yet.");
                          }}
                          className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-emerald-200 hover:bg-emerald-400 hover:text-black"
                        >
                          Approve Payloads
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            const raw = localStorage.getItem("nexorax_integration_payloads");
                            if (!raw) {
                              alert("No integration payloads found.");
                              return;
                            }
                            const blob = new Blob([raw], { type: "application/json" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = "nexorax-integration-payloads.json";
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-fuchsia-200 hover:bg-fuchsia-400 hover:text-black"
                        >
                          Export Payloads
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            const approved = localStorage.getItem("nexorax_integration_payloads_approved") === "true";
                            if (!approved) {
                              alert("Approve payloads first before API staging.");
                              return;
                            }
                            localStorage.setItem("nexorax_external_api_stage_ready", "true");
                            alert("External API stage marked ready. Next step: connect SMTP, WhatsApp Business API, CRM endpoint and invoice provider keys.");
                          }}
                          className="rounded-xl border border-orange-400/30 bg-orange-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-orange-200 hover:bg-orange-400 hover:text-black"
                        >
                          Mark API Stage Ready
                        </button>
                      </div>
                    </div>

                    {(() => {
                      const raw = localStorage.getItem("nexorax_integration_payloads");
                      const payloads = raw ? JSON.parse(raw) : [];
                      return (
                        <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10 bg-black/35 p-4">
                          <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-300">
                            Payload Preview {payloads.length ? `// ${payloads.length} items` : "// none generated yet"}
                          </div>
                          <table className="mt-4 w-full min-w-[860px] text-sm">
                            <thead>
                              <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500">
                                <th className="py-3 text-left">Channel</th>
                                <th className="py-3 text-left">Category</th>
                                <th className="py-3 text-left">Owner</th>
                                <th className="py-3 text-left">Offer</th>
                                <th className="py-3 text-left">Approved</th>
                                <th className="py-3 text-left">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payloads.slice(0, 12).map((item) => (
                                <tr key={item.id} className="border-b border-white/5">
                                  <td className="py-4 font-bold text-white">{item.channel}</td>
                                  <td className="py-4 text-slate-300">{item.category}</td>
                                  <td className="py-4 text-orange-300">{item.owner}</td>
                                  <td className="py-4 text-emerald-300">{item.offer}</td>
                                  <td className="py-4 text-cyan-200">{item.approvedForExternalSend ? "YES" : "NO"}</td>
                                  <td className="py-4 font-black text-cyan-200">{item.status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="mt-5 rounded-2xl border border-white/10 bg-black/35 p-4">

              {(() => {
                const connectorRaw = localStorage.getItem("nexorax_api_connector_status");
                const connectorStatus = connectorRaw ? JSON.parse(connectorRaw) : {};
                const connectorList = [
                  {
                    id: "email",
                    label: "Email",
                    detail: "SMTP / Google Workspace / SendGrid",
                    env: "SMTP_HOST, SMTP_USER, SMTP_PASS or SENDGRID_API_KEY",
                  },
                  {
                    id: "whatsapp",
                    label: "WhatsApp",
                    detail: "WhatsApp Business API token + approved templates",
                    env: "WHATSAPP_TOKEN, WHATSAPP_PHONE_ID, WHATSAPP_TEMPLATE_NAMESPACE",
                  },
                  {
                    id: "crm",
                    label: "CRM",
                    detail: "Lead table, CRM endpoint or internal deal registry",
                    env: "CRM_API_URL, CRM_API_KEY",
                  },
                  {
                    id: "invoice",
                    label: "Invoices",
                    detail: "Stripe / PayPal / accounting connector",
                    env: "STRIPE_SECRET_KEY, PAYPAL_CLIENT_ID, ACCOUNTING_API_KEY",
                  },
                ];

                const getConnector = (id: string) => connectorStatus[id] || {
                  connected: false,
                  tested: false,
                  approved: false,
                };

                const saveConnector = (id: string, update: Record<string, boolean | string>) => {
                  const current = getConnector(id);
                  const nextStatus = {
                    ...connectorStatus,
                    [id]: {
                      ...current,
                      ...update,
                      updatedAt: new Date().toISOString(),
                    },
                  };
                  localStorage.setItem("nexorax_api_connector_status", JSON.stringify(nextStatus));
                  localStorage.setItem("nexorax_audit_logs", JSON.stringify([
                    {
                      type: "API_CONNECTOR_STATUS_UPDATED",
                      connector: id,
                      update,
                      timestamp: new Date().toISOString(),
                    },
                    ...JSON.parse(localStorage.getItem("nexorax_audit_logs") || "[]"),
                  ].slice(0, 200)));
                  setApiConnectorVersion((version) => version + 1);
                };

                const allTested = connectorList.every((connector) => getConnector(connector.id).tested);
                const allApproved = connectorList.every((connector) => getConnector(connector.id).approved);

                return (
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/10 p-6 shadow-[0_0_40px_rgba(16,185,129,0.08)]">
                    <span className="hidden">{apiConnectorVersion}</span>
                    <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="mb-2 text-xs font-black uppercase tracking-[0.35em] text-emerald-300">
                          API CONNECTOR APPROVAL CENTER
                        </p>
                        <h3 className="text-2xl font-black uppercase tracking-[0.08em] text-white">
                          Connect, Test And Approve Real Sending Channels
                        </h3>
                        <p className="mt-3 max-w-4xl text-sm font-bold leading-6 text-slate-300">
                          This stage confirms the API keys and connector routes are ready. It still does not send live emails,
                          WhatsApps, invoices or CRM messages until the final server-side send action is built and approved.
                        </p>
                      </div>
                      <div className={`rounded-xl border px-5 py-3 text-xs font-black uppercase tracking-[0.18em] ${
                        allApproved ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-100" : "border-amber-400/40 bg-amber-500/10 text-amber-200"
                      }`}>
                        {allApproved ? "API STAGE APPROVED" : allTested ? "READY FOR DIRECTOR APPROVAL" : "TESTING REQUIRED"}
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      {connectorList.map((connector) => {
                        const status = getConnector(connector.id);
                        return (
                          <div key={connector.id} className="rounded-xl border border-cyan-400/25 bg-slate-950/70 p-4">
                            <div className="mb-3 flex items-start justify-between gap-4">
                              <div>
                                <h4 className="text-base font-black uppercase tracking-[0.08em] text-white">{connector.label}</h4>
                                <p className="mt-1 text-xs font-bold text-slate-400">{connector.detail}</p>
                                <p className="mt-2 text-[11px] font-black uppercase tracking-[0.12em] text-cyan-200">{connector.env}</p>
                              </div>
                              <div className="text-right text-[11px] font-black uppercase tracking-[0.12em] text-cyan-200">
                                {status.approved ? "APPROVED" : status.tested ? "TESTED" : status.connected ? "CONNECTED" : "NOT CONNECTED"}
                              </div>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-3">
                              <button
                                type="button"
                                onClick={() => saveConnector(connector.id, { connected: true })}
                                className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-cyan-100"
                              >
                                Mark Connected
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!getConnector(connector.id).connected) {
                                    alert("Mark " + connector.label + " connected first.");
                                    return;
                                  }
                                  saveConnector(connector.id, { tested: true });
                                }}
                                className="rounded-lg border border-purple-400/40 bg-purple-500/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-purple-100"
                              >
                                Test Passed
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!getConnector(connector.id).tested) {
                                    alert("Test " + connector.label + " first.");
                                    return;
                                  }
                                  saveConnector(connector.id, { approved: true });
                                }}
                                className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-emerald-100"
                              >
                                Approve
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (!allTested) {
                            alert("All connectors must be marked tested before API approval.");
                            return;
                          }
                          const nextStatus = { ...connectorStatus };
                          connectorList.forEach((connector) => {
                            nextStatus[connector.id] = {
                              ...getConnector(connector.id),
                              approved: true,
                              approvedAt: new Date().toISOString(),
                            };
                          });
                          localStorage.setItem("nexorax_api_connector_status", JSON.stringify(nextStatus));
                          localStorage.setItem("nexorax_external_api_connectors_approved", "true");
                          setApiConnectorVersion((version) => version + 1);
                          alert("API connectors approved for server integration. No external messages have been sent.");
                        }}
                        className="rounded-xl border border-emerald-400/60 bg-emerald-500/20 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-emerald-100"
                      >
                        Approve All Connectors
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const payload = {
                            status: allApproved ? "APPROVED_FOR_SERVER_ENDPOINTS" : "PENDING_CONNECTOR_APPROVAL",
                            connectors: connectorList.map((connector) => ({
                              ...connector,
                              status: getConnector(connector.id),
                            })),
                            exportedAt: new Date().toISOString(),
                          };
                          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "nexorax-api-connector-approval.json";
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="rounded-xl border border-cyan-400/50 bg-cyan-500/10 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-cyan-100"
                      >
                        Export Connector Approval JSON
                      </button>
                    </div>

                    <div className="mt-5 rounded-2xl border border-cyan-500/30 bg-cyan-950/10 p-5">
                      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <p className="mb-2 text-xs font-black uppercase tracking-[0.35em] text-cyan-300">
                            SERVER API STAGE 1
                          </p>
                          <h4 className="text-xl font-black uppercase tracking-[0.08em] text-white">
                            Backend Connector Status And Dry Run
                          </h4>
                          <p className="mt-2 text-sm font-bold leading-6 text-slate-300">
                            These buttons call real server endpoints. They check .env connector readiness and run safe dry-runs only.
                          </p>
                        </div>
                        <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-cyan-100">
                          NO EXTERNAL SEND
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <button
                          type="button"
                          onClick={async () => {
                            const response = await fetch("/api/revenue/connectors/status");
                            const data = await response.json();
                            alert(
                              "Server connector status:\n" +
                              "Email: " + data.connectors.email.mode + "\n" +
                              "WhatsApp: " + data.connectors.whatsapp.mode + "\n" +
                              "CRM: " + data.connectors.crm.mode + "\n" +
                              "Invoice: " + data.connectors.invoice.mode + "\n\n" +
                              data.message
                            );
                          }}
                          className="rounded-xl border border-cyan-400/50 bg-cyan-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-cyan-100"
                        >
                          Check Server Status
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            const checks = ["email", "whatsapp", "crm", "invoice"];
                            const results = [];
                            for (const connector of checks) {
                              const response = await fetch("/api/revenue/connectors/test", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ connector }),
                              });
                              const data = await response.json();
                              results.push(connector.toUpperCase() + ": " + data.status);
                            }
                            alert("Server dry tests complete:\n" + results.join("\n") + "\n\nNo external messages sent.");
                          }}
                          className="rounded-xl border border-purple-400/50 bg-purple-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-purple-100"
                        >
                          Run Connector Tests
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            const response = await fetch("/api/revenue/connectors/stage-approval", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ approved: true }),
                            });
                            const data = await response.json();
                            alert(data.message || "Server API stage approved.");
                          }}
                          className="rounded-xl border border-emerald-400/50 bg-emerald-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-emerald-100"
                        >
                          Approve Server Stage
                        </button>


                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const response = await fetch("/api/revenue/email/test-send", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ testOnly: true }),
                              });
                              const data = await response.json();
                              alert(
                                data.success
                                  ? "Email test sent successfully to: " + data.to + "\n\n" + data.message
                                  : "Email test not sent:\n" + (data.error || "Unknown error") + "\n\nRequired: " + JSON.stringify(data.requiredEnv || [])
                              );
                            } catch (err) {
                              alert("Email test failed before reaching server. Check /api routing and PM2 logs.");
                            }
                          }}
                          className="rounded-xl border border-emerald-400/50 bg-emerald-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-emerald-100"
                        >
                          Send Test Email
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            const queue = JSON.parse(localStorage.getItem("nexorax_crm_send_queue") || "[]");
                            const response = await fetch("/api/revenue/send-queue/dry-run", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ queue }),
                            });
                            const data = await response.json();
                            alert(
                              "Dry run complete.\n" +
                              "Queue items checked: " + data.inputCount + "\n" +
                              "Preview items: " + data.preview.length + "\n\n" +
                              data.message
                            );
                          }}
                          className="rounded-xl border border-amber-400/50 bg-amber-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-amber-100"
                        >
                          Dry Run Send Queue
                        </button>

                      <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-4">
                        <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-cyan-200">
                          Clickable API Fallbacks
                        </p>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <a
                            href="/api/revenue/connectors/status"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-xl border border-cyan-400/50 bg-cyan-500/10 px-4 py-3 text-center text-xs font-black uppercase tracking-[0.14em] text-cyan-100"
                          >
                            Open Status JSON
                          </a>
                          <a
                            href="/api/revenue/connectors/test-all"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-xl border border-purple-400/50 bg-purple-500/10 px-4 py-3 text-center text-xs font-black uppercase tracking-[0.14em] text-purple-100"
                          >
                            Open Test JSON
                          </a>
                          <a
                            href="/api/revenue/connectors/approve-stage"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-xl border border-emerald-400/50 bg-emerald-500/10 px-4 py-3 text-center text-xs font-black uppercase tracking-[0.14em] text-emerald-100"
                          >
                            Open Approval JSON
                          </a>
                          <a
                            href="/api/revenue/send-queue/dry-run-preview"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-xl border border-amber-400/50 bg-amber-500/10 px-4 py-3 text-center text-xs font-black uppercase tracking-[0.14em] text-amber-100"
                          >
                            Open Dry Run JSON
                          </a>
                        </div>
                      </div>
                      </div>
                    </div>

                  </div>
                );
              })()}


                    <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-300">
                      External Connector Requirements
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-[11px] text-slate-300">
                      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                        <strong className="text-white">Email</strong><br />SMTP / Google Workspace / SendGrid key
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                        <strong className="text-white">WhatsApp</strong><br />WhatsApp Business API token + templates
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                        <strong className="text-white">CRM</strong><br />Lead table or CRM endpoint
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                        <strong className="text-white">Invoices</strong><br />Stripe / PayPal / accounting connector
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-white/10 bg-black/35 p-5 overflow-x-auto">
                  <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-300">
                    Category Revenue Preview
                  </div>

                  <table className="mt-4 w-full min-w-[920px] text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="py-3 text-left">Category</th>
                        <th className="py-3 text-left">Nodes</th>
                        <th className="py-3 text-left">Buyer Type</th>
                        <th className="py-3 text-left">Offer Family</th>
                        <th className="py-3 text-left">Admin Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CATEGORIES.slice(0, 18).map((cat, idx) => (
                        <tr key={cat.title || idx} className="border-b border-white/5">
                          <td className="py-4 font-bold text-white">{cat.title}</td>
                          <td className="py-4 text-slate-300">{cat.nodes?.length || 0}</td>
                          <td className="py-4 text-slate-300">{idx % 3 === 0 ? "SME / local business" : idx % 3 === 1 ? "Enterprise / public sector" : "Creator / operator"}</td>
                          <td className="py-4 text-emerald-300">{idx % 2 === 0 ? "Automation / dashboard" : "Website / growth / proposal"}</td>
                          <td className="py-4 font-black text-cyan-200">Generate + Review</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </div>
            ) : adminSubTab === 'CAMPAIGN_DISPATCH' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <CampaignDispatchConsole />
              </div>
            ) : adminSubTab === 'SOCIAL_CONNECTORS' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <SocialConnectorsConsole />
              </div>
            ) : adminSubTab === 'DIGITAL_PRODUCT_FACTORY' ? (
              <div className="max-w-7xl mx-auto w-full mb-10">
                <NexusDigitalProductFactory
                  products={digitalProducts}
                  setProducts={setDigitalProducts}
                  onAddLog={(module, action, status, details) => {
                    console.log(`[${status}] ${module}: ${action} - ${details}`);
                    const savedLogs = localStorage.getItem('nexorax_audit_logs');
                    const logsArr = savedLogs ? JSON.parse(savedLogs) : [];
                    logsArr.unshift({
                      id: `log-${Date.now()}`,
                      timestamp: new Date().toISOString(),
                      module,
                      action,
                      status: status === 'SUCCESS' ? 'SECURED' : status === 'WARNING' ? 'ACTIVE_ROUTING' : 'BLOCKED',
                      details
                    });
                    localStorage.setItem('nexorax_audit_logs', JSON.stringify(logsArr.slice(0, 100)));
                  }}
                  publicInquiries={publicInquiries}
                  setPublicInquiries={setPublicInquiries}
                />
              </div>
            ) : adminSubTab === 'SMTP_EMAIL_TEST' ? (
              <div className="max-w-7xl mx-auto w-full mb-10 text-left">
                <div className="bg-zinc-950 border border-cyan-500/25 p-8 rounded-3xl space-y-4 shadow-2xl">
                  <h2 className="text-xl font-black uppercase text-white tracking-tight flex items-center gap-2">
                    <Mail className="w-6 h-6 text-cyan-400" />
                    <span>SMTP / Email Test Centre</span>
                  </h2>
                  <p className="text-slate-400 text-xs leading-relaxed max-w-2xl">
                    SMTP test tools are admin-only and currently in controlled mode.
                  </p>
                  <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center gap-3">
                    <span className="w-2.5 h-2.5 bg-yellow-500 rounded-full animate-ping" />
                    <span className="text-[10px] font-mono font-black uppercase text-yellow-500">Controlled Mode Active</span>
                  </div>
                </div>
              </div>
            ) : adminSubTab === 'LEGACY_LAB' ? (
              <div className="max-w-7xl mx-auto w-full mb-10 text-left">
                <div className="bg-zinc-950 border border-amber-500/20 p-8 rounded-3xl space-y-6 shadow-2xl">
                  <div className="border-b border-white/5 pb-4">
                    <h2 className="text-xl font-black uppercase text-white tracking-tight flex items-center gap-2">
                      <Lock className="w-6 h-6 text-amber-500" />
                      <span>Legacy &amp; Internal Experimental Lab</span>
                    </h2>
                    <p className="text-slate-400 text-[10.5px] uppercase pt-1">
                      Director-only archived screens and simulation nodes.
                    </p>
                  </div>
                  
                  {/* Inner tabs selector for Legacy Lab */}
                  <div className="flex flex-wrap gap-2 text-[10px] font-mono font-bold uppercase">
                    {[
                      { id: 'sec', label: 'Secretary-Node' },
                      { id: 'matrix', label: 'NexoraX Matrix Categories' },
                      { id: 'divisions', label: 'Company Divisions Directory' },
                      { id: 'reality', label: 'Reality Audit Node' },
                      { id: 'accountant', label: 'Accountant Node' },
                      { id: 'lawyer', label: 'Lawyer Node' },
                      { id: 'architect', label: 'Talk to Architect' },
                      { id: 'protocol', label: 'Protocol Pipeline' },
                      { id: 'invoice', label: 'Invoice Register Candidates' }
                    ].map(t => (
                      <button key={t.id} type="button" onClick={() => setLegacyLabTab(t.id)} className={`py-1.5 px-3 rounded-lg border cursor-pointer ${legacyLabTab === t.id ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'text-slate-500 border-transparent hover:bg-zinc-900'}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>

                  <div className="bg-black/40 border border-zinc-900 p-6 rounded-2xl min-h-[500px]">
                    {legacyLabTab === 'sec' && (
                      <SecretaryNode onNavigate={(tab) => {
                        window.dispatchEvent(new CustomEvent('nexorax-trigger-main-tab', { detail: tab }));
                      }} />
                    )}
                    {legacyLabTab === 'matrix' && (
                      <CosmicTabPanel title="DYNAMIC PAYLOAD MATRIX" />
                    )}
                    {legacyLabTab === 'divisions' && (
                      <CosmicLandingTabPanel title="Company Divisions" onSelectCategory={(cat) => {
                        window.dispatchEvent(new CustomEvent('nexorax-trigger-main-tab', { detail: cat }));
                      }} />
                    )}
                    {legacyLabTab === 'reality' && (
                      <RealityAuditTerminal />
                    )}
                    {legacyLabTab === 'accountant' && (
                      <TaxAccountingNode />
                    )}
                    {legacyLabTab === 'lawyer' && (
                      <div className="p-8 font-mono text-center text-slate-500 space-y-4">
                        <div className="text-white font-bold uppercase">Lawyer Node (Vetting Registry)</div>
                        <div>Compliance framework active. All legal templates verified against standard NI guidelines.</div>
                      </div>
                    )}
                    {legacyLabTab === 'architect' && (
                      <div className="p-8 font-mono text-center text-slate-500 space-y-4">
                        <div className="text-white font-bold uppercase">Architect Voice Assistant</div>
                        <div>Voice mapping and assistant modules are suspended on staging. Use the control console standard hotkeys.</div>
                      </div>
                    )}
                    {legacyLabTab === 'protocol' && (
                      <div className="p-8 font-mono text-center text-slate-500 space-y-4">
                        <div className="text-white font-bold uppercase">Protocol Pipeline (Node-18.5)</div>
                        <div>Zero-Fund services guide active. Verification pipelines initialized.</div>
                      </div>
                    )}
                    {legacyLabTab === 'invoice' && (
                      <div className="p-8 font-mono text-center text-slate-500 space-y-4">
                        <div className="text-white font-bold uppercase">Invoice Register Candidates</div>
                        <div>No pending invoice candidates in registry queue.</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="max-w-7xl mx-auto w-full mb-10 space-y-10">
                  <SecurePaymentLiveBalancePipelineNode />
                  <RevenueTicker />
                  <TaxAccountingNode />
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto w-full">
                  {/* Status Indicators */}
                  <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6">
                    <h3 className="text-cyan-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><Activity className="w-4 h-4"/> STATUS INDICATORS</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { label: 'CPU LOAD', val: '42%', status: 'nominal', color: 'bg-emerald-500' },
                        { label: 'MEMORY', val: '14.2GB', status: 'nominal', color: 'bg-emerald-500' },
                        { label: 'DB LATENCY', val: '18ms', status: 'optimal', color: 'bg-cyan-500' },
                        { label: 'FIREWALL', val: 'STRICT', status: 'active', color: 'bg-rose-500' },
                      ].map((stat, i) => (
                        <div key={i} className="bg-[#050914] border border-slate-800/80 rounded flex flex-col items-center justify-center p-4 text-center gap-2">
                           <span className="text-[9px] text-slate-500 tracking-widest uppercase font-bold">{stat.label}</span>
                           <span className="text-white text-xl font-mono font-bold tracking-widest">{stat.val}</span>
                           <div className="flex items-center gap-2 mt-1">
                             <div className={`w-1.5 h-1.5 rounded-full ${stat.color} animate-pulse shadow-[0_0_8px_currentColor]`}></div>
                             <span className="text-[8px] text-slate-400 uppercase tracking-widest">{stat.status}</span>
                           </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Command Log */}
                  <div className="bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6 lg:col-span-2">
                    <h3 className="text-blue-400 font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><FileText className="w-4 h-4"/> COMMAND LOG</h3>
                    <div className="flex-1 bg-[#050914] border border-slate-800/50 rounded-lg p-4 font-mono text-[9px] sm:text-[10px] leading-relaxed overflow-y-auto max-h-[300px] custom-scrollbar text-slate-400 space-y-2">
                       {[
                         "[SYS] Node 1 (Architect) successfully deployed into cluster.",
                         "[SEC] Incoming ping from 182.72.x.x intercepted and dropped.",
                         "[FIN] Daily Secure Payment Provider reconciliation verified: 100% exact match.",
                         "[SYS] Core 0.8s latency requirement satisfied. Current response: 0.2s",
                         "[SEC] Global copyright marker successfully injected.",
                         "[FIN] 1% Perpetual Levy for Global Aid allocated and transferred.",
                         "[OP] Secure Invoice Register Mandate template generated and cached.",
                         "[SYS] Holographic UI rendered with zero dropped frames.",
                         "[NET] CloudSync-02 Backup synchronized locally and to NexoraX Resource Library."
                       ].map((cmd, i) => (
                         <div key={i} className="flex gap-2">
                           <span className="text-slate-600 shrink-0">[{new Date().toISOString().split('T')[1].substring(0,8)}]</span>
                           <span className={cmd.includes('[SEC]') ? 'text-rose-400' : cmd.includes('[FIN]') ? 'text-amber-400' : 'text-slate-300'}>{cmd}</span>
                         </div>
                       ))}
                    </div>
                  </div>

                  {/* Admin Table */}
                  <div className="lg:col-span-3 bg-black/50 border border-slate-800 rounded-xl p-6 flex flex-col gap-6 w-full">
                     <h3 className="text-[#a855f7] font-bold text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"><Database className="w-4 h-4"/> ADMIN AUTHORIZATION TABLE</h3>
                     <div className="w-full overflow-x-auto">
                       <table className="w-full text-left font-mono text-[10px] uppercase tracking-wider text-slate-400">
                         <thead>
                           <tr className="border-b border-slate-800 text-slate-500">
                             <th className="pb-3 pt-2 px-4 font-bold">Node Identity</th>
                             <th className="pb-3 pt-2 px-4 font-bold">Role</th>
                             <th className="pb-3 pt-2 px-4 font-bold">Access Level</th>
                             <th className="pb-3 pt-2 px-4 font-bold text-center">Status</th>
                             <th className="pb-3 pt-2 px-4 font-bold text-right">Last Sync</th>
                           </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-800/50 block md:table-row-group">
                           {[
                             { id: "Lead Director", role: "Director (Office of)", level: "Tier-1 (Omni)", status: "ACTIVE / VERIFIED", sync: "Just now" },
                             { id: "NODE-16 (The Gavel)", role: "Legal Operations", level: "Tier-2 (Restricted)", status: "STANDBY", sync: "2 mins ago" },
                             { id: "NODE-07 (Accountant)", role: "Fiscal & Levy", level: "Tier-2 (Restricted)", status: "ACTIVE", sync: "15 secs ago" },
                             { id: "Staff Coordinator (PK Node)", role: "Remote Operations", level: "Tier-3 (Clerical)", status: "PENDING ENTERPRISE STANDARDS", sync: "4 hrs ago" },
                             { id: "Communications Associate (PK Node)", role: "Comms & Routing", level: "Tier-3 (Clerical)", status: "PENDING ENTERPRISE STANDARDS", sync: "4 hrs ago" },
                             { id: "Secure Invoice Register Candidate", role: "Sec Ops", level: "Tier-2", status: "AWAITING ACQUISITION", sync: "N/A" }
                           ].map((user, i) => (
                             <tr key={i} className="hover:bg-slate-900/30 transition-colors block md:table-row">
                               <td className="py-3 px-4 text-white font-bold">{user.id}</td>
                               <td className="py-3 px-4 text-cyan-200">{user.role}</td>
                               <td className="py-3 px-4">{user.level}</td>
                               <td className="py-3 px-4 text-center">
                                 <span className={`inline-block px-2 py-0.5 rounded font-bold text-[8px] ${
                                   user.status.includes('ACTIVE') ? 'bg-[#022c15] text-[#10b981]' :
                                   user.status.includes('PENDING') ? 'bg-amber-950 text-amber-400' :
                                   user.status.includes('STANDBY') ? 'bg-slate-800 text-slate-300' :
                                   'bg-rose-950 text-rose-400'
                                 }`}>{user.status}</span>
                               </td>
                               <td className="py-3 px-4 text-right">{user.sync}</td>
                             </tr>
                           ))}
                         </tbody>
                       </table>
                     </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        );
      case 'INVOICE_LEDGER':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 bg-[#080d1e] overflow-y-auto">
            <InvoiceLedger />
          </motion.div>
        );

      case 'DEAL_REGISTRY':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 bg-[#080d1e] overflow-y-auto pb-10">
            <ClientDealRegistry />
          </motion.div>
        );

      case 'GEOGRAPHIC_ANALYTICS': {
        const sectorList = [
          { id: 'UK', name: 'Dunfermline NexoraX Command', flag: '🇬🇧', ipRange: '81.104.22.xxx', activeCandidate: 'Eleanor Vance', x: 382, y: 105, lat: 56.0718, lng: -3.4522, detail: 'Primary candidate staging terminal. Google Drive NexoraX Resource Library backup link secure.', avgLatency: '114ms', conversion: '98.5%', state: 'SECURED' as const },
          { id: 'SecurePayment_BANK', name: 'Secure Payment Provider NexoraX Node', flag: '🏦', ipRange: '185.151.204.xxx', activeCandidate: 'NexoraX Treasurer', x: 380, y: 120, lat: 51.5074, lng: -0.1278, detail: 'Secure Payment Provider NexoraX Direct Settlement Node (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider). Permanent ledger mirroring active.', avgLatency: '15ms', conversion: '100%', state: 'SECURED' as const },
          { id: 'EU', name: 'Frankfurt Outbound Tunnel', flag: '🇩🇪', ipRange: '5.161.42.xxx', activeCandidate: 'System Autonomous', x: 405, y: 128, lat: 50.1109, lng: 8.6821, detail: 'Primary SMTP Mail Transfer Agent (MTA) & click-captures tracking router. 0.8s SLA compliant.', avgLatency: '78ms', conversion: '94.2%', state: 'ACTIVE_ROUTING' as const },
          { id: 'PK', name: 'Pakistan Remote node', flag: '🇵🇰', ipRange: '110.37.12.xxx', activeCandidate: 'Staff Coordinator (PK Node)', x: 538, y: 175, lat: 31.5204, lng: 74.3587, detail: 'Operational clerical sync remote node under Pakistan_Node_Sync protocols. Syncing compliance documents.', avgLatency: '242ms', conversion: '88.3%', state: 'ACTIVE_ROUTING' as const },
          { id: 'US', name: 'North America Edge Point', flag: '🇺🇸', ipRange: '64.225.101.xxx', activeCandidate: 'Claire Jenkins', x: 220, y: 145, lat: 40.7128, lng: -74.0060, detail: 'Atlantic transaction mirroring staging node & direct-billing billing matrix validator.', avgLatency: '128ms', conversion: '91.0%', state: 'SECURED' as const },
          { id: 'ASIA', name: 'Tokyo Transaction Engine', flag: '🇯🇵', ipRange: '122.211.5.xxx', activeCandidate: 'External Client Partners', x: 670, y: 155, lat: 35.6762, lng: 139.6503, detail: 'Far Eastern arbitrage tracker & dual currency levy audit registry.', avgLatency: '194ms', conversion: '86.4%', state: 'ACTIVE_ROUTING' as const },
          { id: 'IN_ROGUE', name: 'Cognitive defense block', flag: '🇮🇳', ipRange: '103.241.12.xxx', activeCandidate: 'Blocked Honeypot Probe', x: 520, y: 195, lat: 12.9716, lng: 77.5946, detail: 'Rogue brand duplicate trackers detected in Bangalore hosting space. Nullified completely via CloudSync-02.', avgLatency: 'N/A', conversion: '0%', state: 'BLOCKED' as const }
        ];

        const activeSectorObj = sectorList.find(s => s.id === selectedSector) || sectorList[0];

        const hourlyChartData = [
          { hour: '00:00', clicks: Math.round((densityWeights[selectedSector] || 50) * 1.2) },
          { hour: '04:00', clicks: Math.round((densityWeights[selectedSector] || 50) * 0.8) },
          { hour: '08:00', clicks: Math.round((densityWeights[selectedSector] || 50) * 2.5) },
          { hour: '12:00', clicks: Math.round((densityWeights[selectedSector] || 50) * 3.8) },
          { hour: '16:00', clicks: Math.round((densityWeights[selectedSector] || 50) * 4.1) },
          { hour: '20:00', clicks: Math.round((densityWeights[selectedSector] || 50) * 2.9) }
        ];

        const categoryChartData = [
          { metric: 'Secure Invoice Register Contracts', volume: Math.round(densityWeights.UK * 15) },
          { metric: 'MNDAs Signed', volume: Math.round(densityWeights.EU * 12) },
          { metric: 'Invoices Paid', volume: Math.round(densityWeights.US * 8) },
          { metric: 'Rogue Interceptors', volume: Math.round(densityWeights.IN_ROGUE * 18) }
        ];

        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-5 md:p-8 bg-gradient-to-b from-[#080d1e] to-[#04060d] overflow-y-auto space-y-6 w-full text-left">
            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-indigo-500/20 pb-4 gap-4">
              <div>
                <span className="text-[10px] font-mono font-black text-[#e840f3] tracking-[0.3em] uppercase block mb-1">
                  Website Engagement & Click Analytics & CLICK ANALYTICS
                </span>
                <h4 className="font-sans font-black text-xl text-white uppercase tracking-wider">
                  GEOGRAPHIC CLICK-THROUGH DENSITY HEATMAP
                </h4>
                <p className="text-slate-400 text-xs font-mono uppercase mt-0.5 max-w-3xl">
                  Monitoring engagement, delivery hot zones, contract triggers, and neutralizing rogue intercepts
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <div className="flex items-center gap-1.5 bg-slate-950/60 border border-indigo-500/30 rounded-xl px-2.5 py-1.5 shadow-[inset_0_0_10px_rgba(30,58,138,0.2)]">
                  <span className="text-[9px] font-mono font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                    <Compass className="w-3 h-3 text-cyan-400" />
                    SECTOR:
                  </span>
                  <select
                    value={selectedBusinessSector}
                    onChange={(e) => changeBusinessSector(e.target.value as any)}
                    className="bg-transparent border-none text-[9px] font-mono font-black text-[#e840f3] uppercase tracking-wider focus:outline-none focus:ring-0 cursor-pointer text-right min-w-[110px]"
                  >
                    <option value="ALL" className="bg-[#04060d] text-[#e840f3] font-bold">ALL SECTORS</option>
                    <option value="CONSTRUCTION" className="bg-[#04060d] text-cyan-400 font-bold">🚧 CONSTRUCTION</option>
                    <option value="TAKEAWAYS" className="bg-[#04060d] text-emerald-400 font-bold">🍕 TAKEAWAYS</option>
                    <option value="FINANCE" className="bg-[#04060d] text-amber-400 font-bold">📈 FINANCE</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={triggerTrafficSimulation}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-pink-950 to-purple-950 hover:from-pink-900 hover:to-purple-900 border border-fuchsia-500/40 rounded-xl text-[10px] font-mono font-black text-fuchsia-300 transition-all shadow-[0_0_15px_rgba(232,64,243,0.15)] active:scale-95"
                >
                  <Zap className="w-3.5 h-3.5 text-fuchsia-400 animate-pulse" />
                  SPAWN HEAVY TRAFFIC SPECTRUM
                </button>
                <button
                  type="button"
                  onClick={resetHeatmapData}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-[10px] font-mono font-black text-zinc-400 hover:text-white transition-all active:scale-95"
                >
                  <RefreshCw className="w-3 h-3" />
                  RESET COLD-LEDGER
                </button>
              </div>
            </div>

            <AnimatePresence>
              {heatmapToast && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-indigo-950/40 border border-[#e840f3]/40 p-3 rounded-xl flex items-center gap-3 text-[10px] font-mono text-[#e840f3] uppercase shadow-[0_0_15px_rgba(232,64,243,0.1)] shrink-0 animate-pulse"
                >
                  <span className="w-2 h-2 rounded-full bg-[#e840f3] shrink-0" />
                  <span className="font-bold">{heatmapToast}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {[
                { key: 'Secure Invoice Register', label: 'Secure Invoice Register CONTRACT TRIGGER', desc: 'Acquisition engagements', activeColor: 'bg-fuchsia-950/40 text-fuchsia-300 border-fuchsia-400' },
                { key: 'MNDA', label: 'MNDA INTEGRATIONS', desc: 'Secure signature clicks', activeColor: 'bg-cyan-950/40 text-cyan-300 border-cyan-400' },
                { key: 'PAYMENT', label: 'SecurePayment RECONCILIATIONS', desc: 'Direct-billing clicks', activeColor: 'bg-emerald-950/40 text-emerald-300 border-emerald-400' },
                { key: 'BLOCK', label: 'ROGUE PENETRATIONS', desc: 'Auto-nullified probes', activeColor: 'bg-red-950/40 text-red-300 border-red-500' }
              ].map((item) => {
                const isItemActive = heatmapFilter === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => setHeatmapFilter(item.key as any)}
                    className={`p-3 border rounded-xl text-left font-mono transition-all duration-300 ${isItemActive ? item.activeColor + ' shadow-[0_0_15px_rgba(255,255,255,0.05)]' : 'bg-[#030611]/60 border-slate-800 text-slate-500 hover:text-slate-300'}`}
                  >
                    <span className="text-[10px] uppercase block tracking-wider font-extrabold">{item.label}</span>
                    <span className="text-[8px] text-slate-500 block truncate uppercase mt-0.5">{item.desc}</span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              <div className="lg:col-span-8 flex flex-col space-y-3">
                <div className="flex justify-between items-center font-mono text-[9px] text-zinc-500">
                  <span>[ LIVE SPATIAL COORDINATES GRID CORE v2 // CALIBRATING SYSTEM DENSITY ]</span>
                  <div className="flex items-center gap-1.5 font-bold">
                    <span>FOCAL POWER:</span>
                    {['NORMAL', 'HIGH', 'EXTREME'].map((intensity) => (
                      <button
                        key={intensity}
                        onClick={() => setFocalIntensity(intensity as any)}
                        className={`px-1.5 py-0.5 rounded text-[8px] border transition-all ${focalIntensity === intensity ? 'bg-[#e840f3] border-[#e840f3] text-black font-black' : 'bg-[#01040a] border-slate-800 text-slate-400 hover:text-white'}`}
                      >
                        {intensity}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-full aspect-[2/1] bg-[#02050c]/98 border border-slate-800 rounded-3xl p-2 relative overflow-hidden group/map shadow-[inset_0_0_40px_rgba(0,0,0,0.8)] min-h-[400px] z-10 transition-all duration-300">
                  {/* Real-time Interactive Leaflet Map Container */}
                  <MapContainer 
                    center={[30, 15]} 
                    zoom={2} 
                    zoomControl={false}
                    scrollWheelZoom={true}
                    style={{ height: '100%', width: '100%', minHeight: '380px', borderRadius: '1.25rem', background: '#02050c' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    />

                    {/* Smooth dynamic view panner */}
                    <RecenterMap lat={activeSectorObj.lat} lng={activeSectorObj.lng} selection={selectedSector} />

                    {/* Glowing heat signature radial zones */}
                    {sectorList.map((node) => {
                      const value = densityWeights[node.id] || 0;
                      if (value === 0) return null;
                      
                      const scaleFactor = focalIntensity === 'EXTREME' ? 18000 : focalIntensity === 'HIGH' ? 11000 : 6000;
                      const radiusInMeters = value * scaleFactor;
                      
                      let zoneColor = "#06b6d4";
                      if (node.id === "UK") zoneColor = "#e840f3";
                      if (node.id === "IN_ROGUE") zoneColor = "#ef4444";
                      if (node.id === "PK") zoneColor = "#10b981";

                      return (
                        <Circle
                          key={`heat-ring-${node.id}`}
                          center={[node.lat, node.lng]}
                          radius={radiusInMeters}
                          pathOptions={{
                            fillColor: zoneColor,
                            fillOpacity: 0.16,
                            color: zoneColor,
                            weight: 1,
                            opacity: 0.35,
                            dashArray: '3, 6'
                          }}
                        />
                      );
                    })}

                    {/* Visual Connection Lines for ACTIVE_ROUTING states to Secure Payment Provider */}
                    {(() => {
                      const SecurePaymentNode = sectorList.find(s => s.id === 'SecurePayment_BANK');
                      if (!SecurePaymentNode) return null;
                      
                      return sectorList
                        .filter(node => node.state === 'ACTIVE_ROUTING' && node.id !== 'SecurePayment_BANK')
                        .flatMap((node) => {
                          const basePos: [number, number][] = [
                            [node.lat, node.lng],
                            [SecurePaymentNode.lat, SecurePaymentNode.lng]
                          ];
                          return [
                            // Base conduit pathway connecting .custom-cyber-marker to Secure Payment Provider
                            <Polyline
                              key={`routing-conduit-${node.id}`}
                              positions={basePos}
                              pathOptions={{
                                color: '#3b82f6', // Active Gateway Wave Blue
                                weight: 2.0,
                                opacity: 0.4,
                                className: 'active-routing-path-base'
                              }}
                            />,
                            // High-voltage dynamic drawing laser trace representing processing packets
                            <Polyline
                              key={`routing-laser-${node.id}`}
                              positions={basePos}
                              pathOptions={{
                                color: '#60a5fa', // Blue-400 core
                                weight: 3.5,
                                opacity: 0.95,
                                className: 'active-routing-path-pulse'
                              }}
                            />
                          ];
                        });
                    })()}

                    {/* Highly stylized cyber node markers */}
                    {sectorList.map((node) => {
                      const isSelected = selectedSector === node.id;
                      const value = densityWeights[node.id] || 0;
                      
                      let pointColor = "#06b6d4";
                      if (node.id === "UK") pointColor = "#e840f3";
                      if (node.id === "IN_ROGUE") pointColor = "#ef4444";
                      if (node.id === "PK") pointColor = "#10b981";
                      if (node.id === "SecurePayment_BANK") pointColor = "#fbbf24";

                      const cyberIcon = createCyberMarker(node.id, pointColor, isSelected, node.state);

                      return (
                        <Marker
                          key={`live-node-${node.id}`}
                          position={[node.lat, node.lng]}
                          icon={cyberIcon}
                          eventHandlers={{
                            click: () => {
                              setSelectedSector(node.id);
                            }
                          }}
                        >
                          <Popup closeButton={false}>
                            <div className="p-3 bg-slate-950/95 text-white font-mono rounded-xl border border-indigo-500/20 text-[10px] space-y-1.5 shadow-[0_0_15px_rgba(0,0,0,0.6)]">
                              <div className="flex items-center gap-1.5 border-b border-indigo-950/40 pb-1 font-black text-cyan-200">
                                <span className="text-xs">{node.flag}</span>
                                <span className="uppercase tracking-wider truncate max-w-[120px]">{node.id} STAGE</span>
                              </div>
                              <p className="text-zinc-400 text-[9px] leading-relaxed italic">{node.name}</p>
                              <div className="flex justify-between items-center text-[9px] pt-1">
                                <span className="text-zinc-500 font-semibold">IP BAND:</span>
                                <span className="font-bold text-slate-300">{node.ipRange}</span>
                              </div>
                              <div className="flex justify-between items-center text-[9px]">
                                <span className="text-zinc-500 font-semibold">CLICK HEAT:</span>
                                <span className="font-black text-fuchsia-400">{value}%</span>
                              </div>
                              <div className="flex justify-between items-center text-[9px]">
                                <span className="text-zinc-500 font-semibold">CONVERSION:</span>
                                <span className="font-bold text-emerald-400">{node.conversion}</span>
                              </div>
                              {node.id !== 'IN_ROGUE' ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchGatewayQr(node.id);
                                  }}
                                  className="w-full mt-2 py-1.5 bg-amber-500/10 hover:bg-amber-500 hover:text-black border border-amber-500/40 text-amber-400 font-bold uppercase tracking-wider text-[8px] rounded transition-all cursor-pointer flex items-center justify-center gap-1"
                                >
                                  <QrCode className="w-3 h-3" />
                                  Empire OS QR Code
                                </button>
                              ) : (
                                <div className="w-full mt-2 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 font-bold uppercase tracking-widest text-[7px] text-center rounded">
                                  DEFENSE ACTIVE: NULLIFIED
                                </div>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>

                  {/* Absolute overlaid structural grid system */}
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(232,64,243,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(232,64,243,0.015)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none z-20"></div>

                  {/* Sector Navigation Quick Buttons */}
                  <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5 max-w-[85%] z-20">
                    {sectorList.map((node) => {
                      const isSelected = selectedSector === node.id;
                      const density = densityWeights[node.id] || 0;
                      return (
                        <button
                          key={node.id}
                          type="button"
                          onClick={() => setSelectedSector(node.id)}
                          className={`px-2 py-1 rounded-xl text-[9px] font-mono tracking-wider transition-all border flex items-center gap-1 ${
                            isSelected
                              ? node.id === 'IN_ROGUE'
                                ? 'bg-red-950/70 border-red-500 text-red-200'
                                : 'bg-fuchsia-950/75 border-fuchsia-500 text-fuchsia-200 shadow-[0_0_12px_rgba(232,64,243,0.3)]'
                              : 'bg-black/95 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                          }`}
                        >
                          <span>{node.flag}</span>
                          <span className="font-extrabold">{node.id}</span>
                          <span className="text-zinc-600">|</span>
                          <span className={density > 80 ? 'text-fuchsia-400 font-bold' : density > 50 ? 'text-cyan-400 font-bold' : 'text-zinc-400'}>
                            {density}%
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Heatmap intensity gradient metadata */}
                  <div className="absolute bottom-3 right-3 bg-black/90 border border-slate-800/80 rounded-xl px-2.5 py-1.5 flex items-center gap-2 text-[8px] font-mono z-30 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                    <span className="text-slate-500 font-bold uppercase tracking-wider">ZONE HEAT:</span>
                    <div className="w-16 h-1.5 rounded bg-gradient-to-r from-cyan-950 via-cyan-500/50 to-[#e840f3]" />
                    <span className="text-zinc-300 font-bold">100%</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 flex flex-col gap-4">
                <div className="bg-[#030611] border border-slate-800 rounded-3xl p-4 md:p-5 flex flex-col space-y-3 font-mono text-left relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-900/10 blur-[40px] pointer-events-none rounded-full" />
                  <span className="text-zinc-500 text-[9px] uppercase tracking-widest font-black">
                    SECTOR INTERROGATION DECK
                  </span>

                  <div className="flex items-center gap-2 border-b border-indigo-950/40 pb-2.5">
                    <span className="text-xl shrink-0">{activeSectorObj.flag}</span>
                    <div className="min-w-0">
                      <h5 className="font-extrabold text-white text-xs truncate uppercase tracking-widest">
                        {activeSectorObj.name}
                      </h5>
                      <span className="text-[8px] text-zinc-500">IP REGISTER LEVEL: {activeSectorObj.ipRange}</span>
                    </div>
                  </div>

                  <div className="space-y-2 text-[10px] bg-slate-950/40 p-3 rounded-2xl border border-indigo-950/20">
                    <div className="flex justify-between items-center border-b border-indigo-950/10 pb-1.5">
                      <span className="text-slate-500">ACTIVE HEAT DENSITY:</span>
                      <span className="text-fuchsia-400 text-xs font-black">{densityWeights[activeSectorObj.id] || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-indigo-950/10 pb-1.5">
                      <span className="text-slate-500">ENGAGED CANDIDATE:</span>
                      <span className="text-white font-bold">{activeSectorObj.activeCandidate}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-indigo-950/10 pb-1.5">
                      <span className="text-slate-500">AVERAGE RESPONSE OFFSET:</span>
                      <span className="text-cyan-400 font-extrabold">{activeSectorObj.avgLatency}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">CLICK CONVERSION RATIO:</span>
                      <span className="text-emerald-400 font-extrabold">{activeSectorObj.conversion}</span>
                    </div>
                    <div className="text-[8px] text-zinc-400 leading-normal pt-2 border-t border-indigo-950/20">
                      {activeSectorObj.detail}
                    </div>
                  </div>

                  {activeSectorObj.id !== 'IN_ROGUE' ? (
                    <button
                      type="button"
                      onClick={() => fetchGatewayQr(activeSectorObj.id)}
                      className="w-full py-2 bg-gradient-to-r from-cyan-500/10 to-indigo-500/10 hover:from-cyan-500/25 hover:to-indigo-500/25 border border-cyan-500/30 hover:border-cyan-400 text-cyan-300 hover:text-white font-bold uppercase tracking-wider text-[9px] rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(6,182,212,0.1)]"
                    >
                      <QrCode className="w-3.5 h-3.5" />
                      Verify NexoraX Routing QR (Node-90 API)
                    </button>
                  ) : (
                    <div className="w-full py-2 bg-rose-500/5 border border-rose-500/20 text-rose-400 font-bold uppercase text-[8px] text-center rounded-xl tracking-wider">
                      Threat Block Active • Honeypot Quarantine
                    </div>
                  )}
                </div>

                <div className="bg-[#030611] border border-slate-800 rounded-3xl p-4 flex-1 flex flex-col space-y-3 font-mono text-left max-h-[220px]">
                  <div className="flex justify-between items-center text-zinc-500 text-[9px] border-b border-indigo-950/45 pb-2">
                    <span className="uppercase tracking-widest font-black">ANALYTICS CLICK LOG</span>
                    <span className="text-emerald-500 font-black animate-pulse uppercase flex items-center gap-1">● DEPLOYED</span>
                  </div>

                  <div className="space-y-2 overflow-y-auto custom-scrollbar flex-1 text-[9px] text-zinc-400">
                    {clickLogs.length === 0 ? (
                      <div className="text-center py-6 text-zinc-600 italic uppercase">Log is currently empty</div>
                    ) : (
                      clickLogs.map((log) => {
                        let statusColor = "text-cyan-400 bg-cyan-950/30 border-cyan-500/10";
                        if (log.status === 'MITIGATED') statusColor = "text-red-400 bg-red-950/30 border-red-500/10";

                        return (
                          <div key={log.id} className="p-2.5 bg-black/60 border border-indigo-950/40 rounded-xl leading-relaxed">
                            <div className="flex justify-between items-center gap-2 mb-1">
                              <span className="text-white font-black truncate">{log.candidate}</span>
                              <span className={`px-1 rounded-[4px] border font-bold text-[7px] ${statusColor}`}>
                                {log.status}
                              </span>
                            </div>
                            <div className="text-[8px] text-zinc-300 mb-0.5">{log.action}</div>
                            <div className="flex justify-between text-[7px] text-zinc-500">
                              <span className="truncate max-w-[70%]">{log.zone}</span>
                              <span>{log.time}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-full shrink-0">
              <div className="bg-[#030611] border border-slate-800 rounded-3xl p-5 md:p-6 space-y-4 text-left flex flex-col">
                <div>
                  <span className="text-zinc-500 font-mono text-[9px] uppercase tracking-widest font-bold block mb-1">
                    TEMPORAL CLICK STABILITY RATIO
                  </span>
                  <h5 className="font-sans font-black text-sm text-white uppercase tracking-wider">
                    HOURLY CLICK-THROUGH VOLATILITY ({selectedSector})
                  </h5>
                </div>

                <div className="h-56 w-full flex-1">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <AreaChart data={hourlyChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorClickVol" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity="0.3"/>
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#0c1938" strokeDasharray="3 3" />
                      <XAxis dataKey="hour" stroke="#4b5563" fontSize={9} />
                      <YAxis stroke="#4b5563" fontSize={9} />
                      <Tooltip contentStyle={{ backgroundColor: "#02050c", border: "1px solid #1e1b4b" }} />
                      <Area type="monotone" dataKey="clicks" stroke="#06b6d4" fillOpacity={1} fill="url(#colorClickVol)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex justify-between items-center text-[8px] font-mono border-t border-indigo-950/30 pt-3 text-zinc-500 uppercase">
                  <span>Current selected heat factor: {densityWeights[selectedSector] || 0}</span>
                  <span className="text-[#e840f3]">0.8s SLA responsive ✔</span>
                </div>
              </div>

              <div className="bg-[#030611] border border-slate-800 rounded-3xl p-5 md:p-6 space-y-4 text-left flex flex-col">
                <div>
                  <span className="text-zinc-500 font-mono text-[9px] uppercase tracking-widest font-bold block mb-1">
                    ACTION METRIC INTENSITY WEIGHT
                  </span>
                  <h5 className="font-sans font-black text-sm text-white uppercase tracking-wider">
                    CLICK-THROUGH DENSITY BY ENGAGEMENT CATEGORY
                  </h5>
                </div>

                <div className="h-56 w-full flex-1">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <BarChart data={categoryChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <XAxis dataKey="metric" stroke="#4b5563" fontSize={9} />
                      <YAxis stroke="#4b5563" fontSize={9} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#02050c", border: "1px solid #1e1b4b" }}
                        labelStyle={{ color: "#ffffff", fontFamily: "monospace", fontSize: 10 }}
                      />
                      <Bar dataKey="volume" radius={[4, 4, 0, 0]}>
                        {categoryChartData.map((entry, index) => {
                          const colors = ["#e840f3", "#06b6d4", "#10b981", "#ef4444"];
                          return (
                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                          )
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex justify-between items-center text-[8px] font-mono border-t border-indigo-950/30 pt-3 text-zinc-500 uppercase">
                  <span>Total sector aggregates loaded</span>
                  <span className="text-cyan-400">Database Mirroring Sync complete ✔</span>
                </div>
              </div>
            </div>
          </motion.div>
        );
      }

      case 'IMPERIAL_VAULT':
      default:
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col flex-1 p-6 sm:p-10 bg-gradient-to-b from-[#080d1e] to-[#04060d]">
            {/* Metric Header */}
            <div className="flex flex-col items-center justify-center mb-16 mt-8 space-y-4">
              <div className="text-[#f59e0b] font-bold text-xs uppercase tracking-[0.3em]">NexoraX Resource Library // TOTAL HARVEST (NODE-27.1)</div>
              <h2 className="text-7xl md:text-[6rem] font-sans font-black text-white tracking-tighter tabular-nums drop-shadow-[0_0_40px_rgba(255,255,255,0.15)] leading-none text-center">
                £1,682,700
              </h2>
              <div className="text-[#a855f7] font-bold text-[10px] uppercase tracking-[0.4em] text-center">GLOBAL REVENUE SECURED</div>
            </div>

            {/* 3 Columns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-auto lg:px-8">
              
              {/* Left Column: Sector Metrics */}
              <div className="flex flex-col bg-black/40 border border-slate-800/80 rounded-xl p-6 shadow-xl relative overflow-hidden">
                <h3 className="text-slate-300 font-bold text-[10px] uppercase tracking-[0.2em] mb-8 relative z-10">SECTOR PERFORMANCE METRICS</h3>
                <div className="space-y-6 relative z-10">
                  {[
                    { name: 'FRANCHISE', value: '£450,200', pct: 92 },
                    { name: 'REAL ESTATE', value: '£380,500', pct: 78 },
                    { name: 'FINANCE', value: '£310,000', pct: 64 },
                    { name: 'HEALTHCARE', value: '£215,000', pct: 45 },
                    { name: 'AGRICULTURE', value: '£185,000', pct: 38 },
                    { name: 'SUPPLY CHAIN', value: '£142,000', pct: 29 },
                  ].map((sector) => (
                    <div key={sector.name} className="flex flex-col gap-2">
                      <div className="flex justify-between items-end">
                        <span className="text-[9px] text-slate-400 tracking-widest">{sector.name}</span>
                        <span className="text-[10px] text-[#f59e0b] font-bold">{sector.value}</span>
                      </div>
                      <div className="w-full bg-[#1e1e2d] h-2 rounded-full overflow-hidden flex relative">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${sector.pct}%` }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className="bg-gradient-to-r from-[#7c3aed] to-[#c084fc] h-full shadow-[0_0_10px_rgba(124,58,237,0.5)]"
                        ></motion.div>
                        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[7px] text-slate-500">{sector.pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Subtle glow behind card */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#7c3aed]/5 blur-[60px] rounded-full pointer-events-none"></div>
              </div>

              {/* Center Column: Spinner */}
              <div className="flex flex-col items-center justify-center min-h-[300px]">
                <div className="relative flex items-center justify-center mb-8">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="w-32 h-32 rounded-full border border-dashed border-[#00c3ff]/40"
                  />
                  <motion.div 
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className="w-40 h-40 rounded-full border border-dashed border-[#00c3ff]/10 absolute"
                  />
                  <div className="absolute w-2 h-2 rounded-full bg-[#00c3ff] shadow-[0_0_15px_rgba(0,195,255,0.8)] animate-ping"></div>
                </div>
                <div className="text-[#00c3ff] font-bold text-[10px] uppercase tracking-[0.3em] drop-shadow-[0_0_8px_rgba(0,195,255,0.4)]">HARVESTING ACTIVE</div>
              </div>

              {/* Right Column: Recent Handshakes */}
              <div className="flex flex-col bg-black/40 border border-emerald-900/40 rounded-xl p-6 shadow-xl h-full pb-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <ShieldCheck className="w-24 h-24 text-emerald-500" />
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-emerald-400 font-bold text-[10px] uppercase tracking-[0.2em]">ALL HANDSHAKES AUTHORIZED & VERIFIED</h3>
                </div>
                
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4 max-h-[320px]">
                  {[
                    { time: '15:23:02 GMT', val: 'AUTHORIZED', amount: '+£14,250', desc: 'IMPERIAL FUSION: APEX <> DESSERT DYNASTY' },
                    { time: '14:18:41 GMT', val: 'AUTHORIZED', amount: '+£4,500', desc: 'NODE-14 (POULTRY) <> NODE-01 (FRANCHISE)' },
                    { time: '13:55:10 GMT', val: 'AUTHORIZED', amount: '+£850', desc: 'NODE-07 (LANDLORD) <> NODE-22 (AGENCY)' },
                    { time: '12:44:05 GMT', val: 'AUTHORIZED', amount: '+£1,250', desc: 'NODE-09 (CONSTRUCTION) <> NODE-03 (REAL ESTATE)' },
                    { time: '11:12:33 GMT', val: 'AUTHORIZED', amount: '+£11,400', desc: 'CAPITAL TRANSFER <> 3% Community Allocation' },
                    { time: '09:05:22 GMT', val: 'AUTHORIZED', amount: '+£3,200', desc: 'NODE-18 (CLINIC) <> NODE-04 (HEALTHCARE)' },
                    { time: '08:42:15 GMT', val: 'AUTHORIZED', amount: '+£5,000', desc: 'GLOBAL SYNC <-> INTERNAL OVERRIDE' },
                  ].map((tx, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex flex-col bg-[#050914]/80 border border-emerald-800/60 rounded p-4 gap-3 relative transition-colors shadow-[0_0_15px_rgba(16,185,129,0.05)]"
                    >
                       {/* Top Row */}
                       <div className="flex justify-between items-center w-full">
                         <span className="text-[9px] text-emerald-500/80 tracking-wider font-bold">{tx.time}</span>
                         <span className="text-[10px] text-emerald-400 font-bold tracking-wider relative flex items-center gap-1.5">
                           <CheckCircle className="w-3 h-3" />
                           {tx.val} <span className="opacity-50 mx-1">|</span> {tx.amount}
                           <div className="absolute inset-0 bg-emerald-400/10 blur-[8px] -z-10"></div>
                         </span>
                       </div>
                       {/* Description Row */}
                       <div className="text-[9px] text-emerald-100 tracking-[0.1em] font-medium max-w-[90%] leading-relaxed">
                         {tx.desc}
                       </div>
                       <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-emerald-500/80 to-transparent opacity-100 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
                    </motion.div>
                  ))}
                </div>
                
              </div>

            </div>
          </motion.div>
        );
    }
  };

  return (
    <div className="w-full h-full min-h-[700px] flex flex-col bg-[#050914] border border-slate-800 rounded-xl overflow-hidden font-mono mt-4 relative z-10 shadow-2xl">
      {/* Top Header */}
      <div className="flex flex-col xl:flex-row justify-between items-center border-b border-slate-800 p-6 gap-6 xl:gap-0 bg-[#080d1e] w-full">
        {/* Left Brand Area */}
        <div className="flex flex-col items-start select-none shrink-0">
          <div className="text-white font-sans font-black text-xs uppercase tracking-[0.25em] leading-tight">
            NEXORAX CORE NODE //
          </div>
          <div className="text-[#10b981] font-extrabold text-xs uppercase tracking-[0.2em] mt-1">
            [ONLINE]
          </div>
        </div>

        {/* Center Grid of Nodes with inline Auto-Refresh countdown */}
        <div className="flex items-center gap-4 border border-slate-800/80 bg-black/45 rounded-xl p-3 xl:p-4 max-w-5xl">
          {/* Auto refresh countdown segment */}
          <div className="flex flex-col items-center justify-center border-r border-slate-800/60 pr-4 shrink-0">
            <div className="text-[7.5px] text-slate-500 font-extrabold tracking-widest leading-none text-center">AUTO-</div>
            <div className="text-[7.5px] text-slate-500 font-extrabold tracking-widest leading-none mt-0.5 text-center">REFRESH</div>
            <div className="w-10 h-0.5 bg-slate-900 rounded overflow-hidden mt-1.5 mb-1.5">
              <div 
                className="h-full bg-[#10b981] transition-all duration-1000 ease-linear shadow-[0_0_8px_rgba(16,185,129,0.7)]" 
                style={{ width: `${(refreshCounter / 10) * 100}%` }}
              ></div>
            </div>
            <div className="text-[#10b981] font-mono font-bold text-[10px] leading-none flex items-center justify-center">
              {refreshCounter.toString().padStart(2, '0')}s
            </div>
          </div>

          {/* Matrix of Sub-nodes */}
          <div className="flex flex-col gap-1 w-full">
            {/* ROW 1 */}
            <div className="flex flex-wrap justify-center items-center gap-1 xl:gap-3 py-0.5 border-b border-slate-900/40 pb-1.5">
              {[
                { id: 'NEXORAX_BANK', label: '01. NEXORAX BANK' },
                { id: 'IMPERIAL_VAULT', label: '02. NexoraX Resource Library' },
                { id: 'MANAGEMENT_DECK', label: '03. MANAGEMENT DECK' },
                { id: 'BRAND_HUB', label: '04. BRAND HUB' },
                { id: 'CATEGORIES', label: '05. CATEGORIES' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundImage: 'repeating-linear-gradient(90deg, #10b981, #10b981 4px, #089f6d 4px, #089f6d 5px)',
                          backgroundSize: '100% 100%',
                        }
                      : undefined
                  }
                  className={`px-2 xl:px-3 py-1 rounded text-[8px] xl:text-[9.5px] tracking-wider uppercase font-extrabold transition-all duration-150 cursor-pointer ${
                    activeTab === tab.id
                      ? 'text-black shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-[1.03]'
                      : 'text-slate-500 hover:text-slate-300 bg-transparent hover:bg-slate-900/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ROW 2 */}
            <div className="flex flex-wrap justify-center items-center gap-1 xl:gap-3 py-0.5 border-b border-slate-900/40 py-1.5">
              {[
                { id: 'SYSTEM_STATE', label: '06. SYSTEM STATE' },
                { id: 'SITE_EDITOR', label: '07. SITE EDITOR' },
                { id: 'Secure Invoice Register_OPERATIONS', label: '08. Secure Invoice Register OPERATIONS' },
                { id: 'BRANDING_COMMAND', label: '09. BRANDING COMMAND' },
                { id: 'ADMIN_PANEL', label: '10. ADMIN PANEL' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundImage: 'repeating-linear-gradient(90deg, #10b981, #10b981 4px, #089f6d 4px, #089f6d 5px)',
                          backgroundSize: '100% 100%',
                        }
                      : undefined
                  }
                  className={`px-2 xl:px-3 py-1 rounded text-[8px] xl:text-[9.5px] tracking-wider uppercase font-extrabold transition-all duration-150 cursor-pointer ${
                    activeTab === tab.id
                      ? 'text-black shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-[1.03]'
                      : 'text-slate-500 hover:text-slate-300 bg-transparent hover:bg-slate-900/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ROW 3 */}
            <div className="flex flex-wrap justify-center items-center gap-1 xl:gap-3 py-0.5 border-b border-slate-900/40 py-1.5">
              {[
                { id: 'INVOICE_LEDGER', label: '11. INVOICE LEDGER' },
                { id: 'DEAL_REGISTRY', label: '12. DEAL REGISTRY' },
                { id: 'RECONNAISSANCE_NODE', label: '13. RECONNAISSANCE NODE' },
                { id: 'VIDEO_GENERATOR', label: '14. CINEMATIC VIDEO FORGE' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundImage: 'repeating-linear-gradient(90deg, #10b981, #10b981 4px, #089f6d 4px, #089f6d 5px)',
                          backgroundSize: '100% 100%',
                        }
                      : undefined
                  }
                  className={`px-2 xl:px-3 py-1 rounded text-[8px] xl:text-[9.5px] tracking-wider uppercase font-extrabold transition-all duration-150 cursor-pointer ${
                    activeTab === tab.id
                      ? 'text-black shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-[1.03]'
                      : 'text-slate-500 hover:text-slate-300 bg-transparent hover:bg-slate-900/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ROW 4 */}
            <div className="flex flex-wrap justify-center items-center gap-1 xl:gap-3 py-0.5 border-b border-slate-900/40 py-1.5">
              {[
                { id: 'VOICE_GENERATOR', label: '15. NEURAL TTS FORGE' },
                { id: 'GEOGRAPHIC_ANALYTICS', label: '16. GEOGRAPHIC HEATMAP' },
                { id: 'REALITY_AUDIT', label: '17. REALITY AUDIT (V.1)' },
                { id: 'HERMES_GRID', label: '18. HERMES ACQUISITION GRID' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundImage: 'repeating-linear-gradient(90deg, #10b981, #10b981 4px, #089f6d 4px, #089f6d 5px)',
                          backgroundSize: '100% 100%',
                        }
                      : undefined
                  }
                  className={`px-2 xl:px-3 py-1 rounded text-[8px] xl:text-[9.5px] tracking-wider uppercase font-extrabold transition-all duration-150 cursor-pointer ${
                    activeTab === tab.id
                      ? 'text-black shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-[1.03]'
                      : 'text-slate-500 hover:text-slate-300 bg-transparent hover:bg-slate-900/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ROW 5 */}
            <div className="flex flex-wrap justify-center items-center gap-1 xl:gap-3 pt-1.5">
              {[
                { id: 'NEXORAX_ENGINE', label: '19. THE NEXORAX ENGINE' },
                { id: 'NEXORAX_GENERATOR', label: '20. THE NEXORAX GENERATOR' },
                { id: 'COMMAND_LEDGER', label: '21. COMMAND LEDGER' },
                { id: 'FUNDING_GRANTS', label: '22. FUNDING & GRANTS' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundImage: 'repeating-linear-gradient(90deg, #10b981, #10b981 4px, #089f6d 4px, #089f6d 5px)',
                          backgroundSize: '100% 100%',
                        }
                      : undefined
                  }
                  className={`px-2 xl:px-3 py-1 rounded text-[8px] xl:text-[9.5px] tracking-wider uppercase font-extrabold transition-all duration-150 cursor-pointer ${
                    activeTab === tab.id
                      ? 'text-black shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-[1.03]'
                      : 'text-slate-500 hover:text-slate-300 bg-transparent hover:bg-slate-900/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Contact Info */}
        <div className="flex flex-col text-right justify-center space-y-1.5 select-none shrink-0">
          <div className="text-[9px] text-slate-500 tracking-widest font-extrabold uppercase">
            HOTLINE: <span className="text-white font-black ml-1">08000 622827</span>
          </div>
          <div className="text-[8.5px] text-slate-500 tracking-widest font-extrabold uppercase leading-none">
            EMAIL:
          </div>
          <div className="text-[9.5px] text-white font-black tracking-wider uppercase leading-tight font-mono">
            SUPPORT@NEXORAXDIGITAL.CO.UK
          </div>
        </div>
      </div>

      {/* Overrides and Hotkeys Segment */}
      <div className="flex flex-col xl:flex-row justify-between items-stretch gap-6 border-b border-slate-800 p-6 bg-black/40">
        
        {/* Core Overrides */}
        <div className="flex flex-col md:flex-row items-start gap-4 lg:gap-6 min-w-[320px]">
          <div className="flex items-center gap-2 mt-3 select-none flex-shrink-0">
            <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(6,182,212,0.8)]"></span>
            <span className="text-slate-500 font-extrabold text-[9px] uppercase tracking-[0.2em]">● CORE OVERRIDES</span>
          </div>
          
          <div className="flex flex-col gap-3 w-full">
            <button 
              onClick={onShowFiscalStrike}
              className="bg-[#050914] border border-cyan-500 text-cyan-400 px-6 py-2.5 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] shadow-[0_0_10px_rgba(6,182,212,0.15)] hover:bg-cyan-900/25 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:text-cyan-300 transition-all cursor-pointer text-left focus:outline-none"
            >
              [ ACCOUNTANT NODE ]
            </button>
            <button 
              onClick={onShowLegalStrike}
              className="bg-[#050914] border border-amber-500 text-amber-500 px-6 py-2.5 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] shadow-[0_0_10px_rgba(245,158,11,0.15)] hover:bg-amber-900/25 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:text-amber-400 transition-all cursor-pointer text-left focus:outline-none"
            >
              [ LAWYER NODE ]
            </button>
            <button 
              onClick={onShowArchitectVoice}
              className="bg-[#050914] border border-indigo-505 text-indigo-400 px-6 py-2.5 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] shadow-[0_0_10px_rgba(99,102,241,0.15)] hover:bg-indigo-900/25 hover:shadow-[0_0_20px_rgba(99,102,241,0.4)] hover:text-indigo-300 transition-all cursor-pointer text-left focus:outline-none"
            >
              [ TALK TO ARCHITECT ]
            </button>
          </div>
        </div>

        {/* Engine Hot Keys */}
        <div className="flex-1 flex flex-col border border-fuchsia-500/20 bg-[#050914] p-4 rounded-xl shadow-[0_0_15px_rgba(217,70,239,0.05)] w-full">
          <div className="flex items-center gap-2 mb-4 select-none">
            <span className="text-fuchsia-500 font-extrabold text-[9.5px] uppercase tracking-[0.2em]">⚙ ENGINE HOT KEYS</span>
          </div>

          {/* ROW 1 */}
          <div className="flex flex-wrap gap-3 mb-3">
            <button 
              onClick={() => {
                const receipt = EmpireExpansionEngine.processNodeTransaction(2500, 50);
                const extraDetails = EmpireExpansionEngine.getNodeArchitectureDetails(50);
                setActiveReceipt(`[EMPIRE COMMAND OVERRIDE - NODE 50: ${receipt.nodeName}]\nReceipt Generated:\n${JSON.stringify({ ...receipt, deepArchitecture: extraDetails }, null, 2)}`);
              }}
              className="bg-[#050914] border border-purple-500 text-purple-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-purple-900/25 hover:text-purple-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-purple-550"
            >
              [ NODE 50 ]
            </button>
            <button 
              onClick={() => {
                const receipt = NexoraxNode51Engine.processCommercialLicenseSale(5000);
                const sweepData = NexoraxNode51Engine.executeAutonomicSystemSweep();
                setActiveReceipt(`[NODE 51 COMMERCIAL ENGINE]\nReceipt Generated:\n${JSON.stringify({ ...receipt, autonomicSweepData: sweepData }, null, 2)}`);
              }}
              className="bg-[#050914] border border-fuchsia-500 text-fuchsia-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-fuchsia-900/25 hover:text-fuchsia-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-fuchsia-550"
            >
              [ NODE 51 ]
            </button>
            <button 
              onClick={() => {
                const receipt = AscensionEngine.processAscensionTransaction("Astraea Multiversal Holdings", 500000);
                setActiveReceipt(`[NODE 52 ASCENSION ENGINE]\nReceipt Generated:\n${JSON.stringify(receipt, null, 2)}`);
              }}
              className="bg-[#050914] border border-teal-500 text-teal-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-teal-900/25 hover:text-teal-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-teal-550"
            >
              [ NODE 52 ]
            </button>
            <button 
              onClick={() => setActiveTab('HERMES_GRID')}
              className="bg-[#050914] border border-emerald-500 text-emerald-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-emerald-950/25 hover:text-emerald-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-550"
            >
              [ NODE 81 ]
            </button>
          </div>

          {/* ROW 2 */}
          <div className="flex flex-wrap gap-3 mb-3">
            <button 
              onClick={() => setActiveTab('NEXORAX_ENGINE')}
              className="bg-[#050914] border border-orange-500 text-orange-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-orange-950/25 hover:text-orange-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-orange-550"
            >
              [ NODE 84 ]
            </button>
            <button 
              onClick={() => setActiveTab('NEXORAX_GENERATOR')}
              className="bg-[#050914] border border-fuchsia-400 text-fuchsia-300 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-fuchsia-950/25 hover:text-fuchsia-200 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-fuchsia-550"
            >
              [ NODE 85 ]
            </button>
            <button 
              onClick={() => setActiveTab('COMMAND_LEDGER')}
              className="bg-[#050914] border border-emerald-500 text-emerald-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-emerald-900/25 hover:text-emerald-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-555"
            >
              [ NODE 21 - COMMAND LEDGER ]
            </button>
            <button 
              onClick={() => {
                const record = BillingMatrix.calculateContractManifest("Vanguard Infrastructure", 250000, "MONTHLY");
                setActiveReceipt(`[HYBRID BILLING CORE]\nContract Manifest Generated:\n${JSON.stringify(record, null, 2)}`);
              }}
              className="bg-[#050914] border border-blue-500 text-blue-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.15em] hover:bg-blue-900/25 hover:text-blue-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-550"
            >
              [ INITIATE MONTHLY BILLING ]
            </button>
          </div>

          {/* ROW 3 */}
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => {
                const record = BillingMatrix.calculateContractManifest("Vanguard Infrastructure", 250000, "YEARLY");
                setActiveReceipt(`[HYBRID BILLING CORE]\nContract Manifest Generated:\n${JSON.stringify(record, null, 2)}`);
              }}
              className="bg-[#050914] border border-indigo-500 text-indigo-400 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-[0.15em] hover:bg-indigo-900/25 hover:text-indigo-300 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-550"
            >
              [ INITIATE YEARLY BILLING ]
            </button>
          </div>
        </div>
      </div>

      {/* Ticker */}
      <div className="flex items-center gap-3 border-b border-slate-800 p-2 bg-[#02040a] text-[9.5px] tracking-wider uppercase font-black select-none">
        <span className="w-1.5 h-1.5 bg-fuchsia-500 rounded-full animate-pulse ml-2 shadow-[0_0_8px_rgba(217,70,239,0.9)]"></span>
        <span className="text-fuchsia-500 tracking-[0.15em]">● LIVE TELEMETRY</span>
        <span className="text-slate-400 font-bold ml-2">PERPETUAL LEVY CAPTURED: 1% PERMANENT DISTRIBUTION INITIALIZED // GUARDIAN SYNC NORMAL // ACTIVE DEFENSE ONLINE</span>
      </div>

      {/* Main Content Area */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={activeTab}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 10 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col flex-1"
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>

      {/* Receipt Modal */}
      <AnimatePresence>
        {activeReceipt && (
          <div 
            className="absolute inset-0 z-[50] flex items-center justify-center p-4 lg:p-8 bg-black/90 backdrop-blur-md"
            onClick={() => setActiveReceipt(null)}
          >
            <div className="absolute top-4 right-4 z-[60]">
              <button 
                onClick={() => setActiveReceipt(null)}
                className="bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white px-6 py-3 border border-red-500/50 rounded font-mono text-sm tracking-widest transition-all cursor-pointer shadow-[0_0_15px_rgba(239,68,68,0.4)] flex items-center gap-2 font-bold"
              >
                [ X ] TERMINATE VIEW
              </button>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-4xl bg-[#050914] border border-fuchsia-500 rounded-xl shadow-[0_0_50px_rgba(217,70,239,0.3)] overflow-hidden flex flex-col pointer-events-auto max-h-full my-auto mt-16"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center bg-fuchsia-900/20 p-5 border-b border-fuchsia-500 flex-shrink-0">
                <span className="text-fuchsia-400 font-bold font-mono tracking-widest text-sm sm:text-base flex items-center gap-3">
                  <Database className="w-6 h-6" /> 
                  EMPIRE RECEIPT & SYSTEM DIAGNOSTICS
                </span>
              </div>
              <div className="p-6 overflow-y-auto custom-scrollbar bg-black/80 flex-1">
                <pre className="text-fuchsia-300 font-mono text-[10px] sm:text-xs md:text-sm whitespace-pre-wrap break-words leading-relaxed">
                  {activeReceipt}
                </pre>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Empire OS QR Code Modal */}
      <AnimatePresence>
        {activeQrModalData?.isOpen && (
          <div 
            className="absolute inset-0 z-[50] flex items-center justify-center p-4 lg:p-8 bg-black/95 backdrop-blur-md"
            onClick={() => setActiveQrModalData(null)}
          >
            <div className="absolute top-4 right-4 z-[60]">
              <button 
                onClick={() => setActiveQrModalData(null)}
                className="bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white px-5 py-2.5 border border-red-500/50 rounded font-mono text-xs tracking-widest transition-all cursor-pointer shadow-[0_0_15px_rgba(239,68,68,0.4)] flex items-center gap-2 font-bold uppercase"
              >
                [ x ] CLOSE DIALOG
              </button>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-sm bg-[#050914] border border-amber-500/50 rounded-xl shadow-[0_0_40px_rgba(245,158,11,0.25)] overflow-hidden flex flex-col pointer-events-auto text-left"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-amber-950/20 p-5 border-b border-amber-500/40 flex items-center gap-3">
                <QrCode className="w-5 h-5 text-amber-500 animate-pulse" />
                <div className="font-mono text-xs">
                  <span className="text-[10px] text-amber-400 font-extrabold uppercase tracking-widest block select-text">
                    In the name of Allah, the Greatest and Most Merciful.
                  </span>
                  <span className="text-white font-bold uppercase tracking-wider">
                    EMPIRE OS CORE REGISTRY GENERATOR
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 bg-black/50 space-y-5 text-center font-mono">
                {activeQrModalData.isLoading ? (
                  <div className="py-12 space-y-4 flex flex-col items-center justify-center">
                    <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
                    <span className="text-[10px] text-zinc-400 tracking-widest uppercase animate-pulse">
                      Querying Gateway Router • Latency SLA (0.8s)...
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-amber-400 uppercase tracking-widest">
                        {activeQrModalData.label} VERIFIED MATRIX
                      </h4>
                      <p className="text-[9px] text-slate-400 uppercase">
                        NexoraX Registry Node: {activeQrModalData.gateway.toUpperCase()}_GATEWAY
                      </p>
                    </div>

                    {/* QR Canvas */}
                    <div className="flex flex-col items-center justify-center bg-white p-4 rounded-xl border border-slate-800 w-52 h-52 mx-auto relative shadow-[0_0_25px_rgba(255,255,255,0.05)]">
                      {/* Real Image Fallback */}
                      <img 
                        src={`/${activeQrModalData.qrAsset}`} 
                        alt={`${activeQrModalData.label} QR`}
                        className="absolute inset-0 w-full h-full object-contain p-4 z-10 bg-white"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      
                      {/* Generative Vector QR */}
                      <svg className="w-[180px] h-[180px]" viewBox="0 0 256 256">
                        <rect width="100%" height="100%" fill="#ffffff" />
                        {(() => {
                          const blocks = generateQRBlocks(activeQrModalData.link);
                          const size = blocks.length;
                          const boxSize = 256;
                          const padding = 20;
                          const cellSize = (boxSize - padding * 2) / size;

                          return blocks.map((row, r) => {
                            return row.map((isActive, c) => {
                              if (!isActive) return null;
                              const isFinderPattern = (r < 7 && (c < 7 || c >= size - 7)) || (r >= size - 7 && c < 7);
                              const fillColor = isFinderPattern ? '#06b6d4' : '#0f172a';
                              return (
                                <rect
                                  key={`${r}-${c}`}
                                  x={padding + c * cellSize}
                                  y={padding + r * cellSize}
                                  width={cellSize - 0.5}
                                  height={cellSize - 0.5}
                                  rx={1.2}
                                  fill={fillColor}
                                />
                              );
                            });
                          });
                        })()}
                        <circle cx={128} cy={128} r={14} fill="#0f172a" />
                        <circle cx={128} cy={128} r={9} fill="#f59e0b" stroke="#ffffff" strokeWidth={1.5} />
                        <text x={128} y={128 + 2.5} fontFamily="monospace" fontSize={7} fontWeight="black" fill="#ffffff" textAnchor="middle">NX</text>
                      </svg>
                    </div>

                    <div className="bg-slate-950/60 border border-slate-800/80 rounded-lg p-3 text-left text-[9px] uppercase leading-relaxed text-slate-300">
                      <div className="font-bold text-amber-500 border-b border-slate-900 pb-1 mb-1">
                        PROTOCOL METRICS
                      </div>
                      <p className="font-bold text-white mb-1">{activeQrModalData.note}</p>
                      <p className="text-[8px] text-slate-500 leading-normal lowercase">
                        All routing undergoes automated 1% Community Allocation extraction and 20% Lifetime corporate tax mapping mapped securely to our central ledger.
                      </p>
                    </div>

                    <div className="flex gap-2.5">
                      <a
                        href={activeQrModalData.link}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-[10px] tracking-widest text-center uppercase rounded-lg transition-all shadow-[0_0_15px_rgba(245,158,11,0.2)] flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <Cpu className="w-3.5 h-3.5 shrink-0" />
                        Launch Verification Gate
                      </a>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

